import React from 'react'
import DOMPurify from 'dompurify'
import { Link } from 'react-router-dom'
import { ArrowRight, BarChart3, ChevronDown, CircleHelp, Package, SlidersHorizontal, Sparkles, Target, TrendingUp } from 'lucide-react'
import ClientDashboardV3Layout from './ClientDashboardV3Layout'
import CurrentClientProvider from '../../../providers/CurrentClientProvider'
import AuthContext from '../../../providers/AuthProvider'
import GlobalTip from '../../../providers/GlobalTip'
import EpochMonthService from '../../../services/EpochMonth.service'
import PerformanceDashboardService from '../../../services/dashboards/PerformanceDashboard.service'
import ReportService from '../../../services/reports/Report.service'
import ReportCatalogService from '../../../services/ReportCatalogService'
import ClientDashboardNotificationService from '../../../services/ClientDashboardNotificationService'
import { trackEvent } from '../../../services/AmplitudeService'
import {
  buildV3ClientDashboardPerformancePath,
  buildV3ClientIndieAiPath,
  buildV3ClientDashboardReportsPath,
} from '../../../services/ClientDashboardV3'
import { requestOrbitIndiePanelOpen } from '../../../services/orbit/orbitPageAssistantBridge'
import {
  ClientLevelItem,
  CategoryLevelItem,
  ClassLevelItem,
} from '../../../interfaces/dashboards/PerformanceDashboard.interface'
import { HealthCashFlowSection, type HealthScoreSummaryForHost } from './components/HealthCashFlowSection'
import SalesDynamicTrendModal from './components/SalesDynamicTrendModal'
import KpiHelpIcon, { type KpiTooltipKey } from './components/KpiHelpIcon'
import DashboardFilterBar from './components/DashboardFilterBar'
import SalesAndInventoryPerformanceSection, { SalesAndInventoryView } from './components/SalesAndInventoryPerformanceSection'
import {
  buildDashboardFilterRequestParams,
  createEmptyDashboardFilters,
  DashboardFilterOption,
  serializeDashboardFilters,
} from '../../../services/dashboards/dashboardFilters'

export interface InventoryHealthRow {
  locationCode?: string | null;
  locationName?: string | null;
  category: string;
  className: string;
  classCode: string;
  salesRetail: number;
  markdownsRetail: number;
  inventoryRetail: number;
  inventoryTrendRetail: number;
  inventoryPlanRetail: number;
  onOrderRetail: number;
  onOrderCost: number;
  otbRetail: number;
  otbCost: number;
  buyingOpportunityScore: number;
}

export interface SellThroughRow {
  locationCode?: string | null;
  locationName?: string | null;
  category: string;
  className: string;
  classCode: string;
  sales: number;
  inflowRetail: number;
  beginningInventory: number;
  mmuPercent: number;
  sellThroughRate: number;
  compToLyPercent: number;
  sellThroughGoal: number;
}

interface AdvisorInsight {
  title: string;
  detail: string;
  toneClassName: string;
}

interface JourneyMilestone {
  label: string;
  detail: string;
  status: 'live' | 'watch' | 'next';
}

interface DashboardNotification {
  id: number;
  title: string;
  message: string;
  notification_type: 'advisor' | 'journey' | 'event';
  event_date?: string | null;
  release_at?: string | null;
  created_at?: string | null;
}

interface ReportInsightRow {
  id: string;
  title: string;
  icon: React.ElementType;
  iconWrapperClassName: string;
  iconClassName: string;
  badgeClassName: string;
  badgeLabel: string;
  openReport?: string;
  badgeSparkle?: boolean;
}

interface RecapTrendPoint {
  current: number;
  comparison: number;
}

interface RecapCardModel {
  label: string;
  value: string;
  comparison: string;
  comparisonPositive: boolean;
  trend: RecapTrendPoint[];
  trendLabel?: string | null;
  planValue: string | null;
  planAchievementPercent: number | null;
  lastYearValue: string | null;
  lastYearAchievementPercent: number | null;
}

interface AchievementBarModel {
  scaleMax: number;
  fillPercent: number;
  thresholdPercent: number;
  showOverflowThreshold: boolean;
}

interface ClientProgressRecapData {
  salesRm: number;
  salesLm: number;
  salesPlanRm: number;
  salesPlanLm: number;
  salesRmLy: number;
  salesLmLy: number;
  markdownsRm: number;
  markdownsLm: number;
  markdownsPlanRm: number;
  markdownsPlanLm: number;
  markdownsRmLy: number;
  markdownsLmLy: number;
  inventoryRm: number;
  inventoryRmLy: number;
  inventoryBomLm: number;
  inventoryBomPlanLm: number;
  inventoryBomLmLy: number;
  inventoryBomPlanNm: number;
  inflowRetail_RM?: number;
  inflowRetailRm: number;
  inflowRetailLm: number;
  inflowPlanRetailRm: number;
  inflowPlanRetailLm: number;
  inflowRetailRmLy: number;
  inflowRetailLmLy: number;
}

interface OpenToBuyKpiData {
  plannedRetail: number;
  plannedCost: number;
  onOrderRetail: number;
  onOrderCost: number;
  remainingRetail: number;
  remainingCost: number;
  rowCount: number;
}

type OpenToBuyValue = 'retail' | 'cost'
type OpenToBuyCalculationMode = 'mtd' | 'eom'

const MONTHLY_RECAP_RANGES = ['MTD', 'Last month', 'Last 3M', 'Last 12M'] as const
export const MONTHLY_RECAP_VISIBLE_RANGES = ['MTD', 'Last month'] as const
const MONTHLY_RECAP_COMPARISONS = ['vs Plan', 'vs LY'] as const
const SALES_DYNAMIC_TREND_ELIGIBLE_POS_IDS = new Set([1200, 1201, 1202, 35])

type MonthlyRecapRange = (typeof MONTHLY_RECAP_RANGES)[number]
type MonthlyRecapComparison = (typeof MONTHLY_RECAP_COMPARISONS)[number]
type SalesTrendRange = 'ytd' | '6m' | '12m'

const MONTHLY_RECAP_RANGE_MONTHS: Record<MonthlyRecapRange, number> = {
  MTD: 1,
  'Last month': 1,
  'Last 3M': 3,
  'Last 12M': 12,
}

const ACHIEVEMENT_BAR_OVERFLOW_VISUAL_CAP = 10

export function resolveDashboardEpochMonth(baseEpochMonth: number, selectedEndingPeriod: string): number {
  if (!selectedEndingPeriod) return baseEpochMonth
  const selectedEpochMonth = Number(selectedEndingPeriod)
  return Number.isInteger(selectedEpochMonth) && selectedEpochMonth >= 0
    ? selectedEpochMonth
    : baseEpochMonth
}

interface SalesTrendSummaryCard {
  label: string;
  value: string;
  sub: string;
  tone: string;
}

export function resolveSalesTrendPeriod(range: SalesTrendRange, lastCompletedEpochMonth: number) {
  const lastCompletedDate = EpochMonthService.epochMonthToDate(lastCompletedEpochMonth)
  const startEpochMonth = range === 'ytd'
    ? lastCompletedEpochMonth - lastCompletedDate.getMonth()
    : lastCompletedEpochMonth - (range === '6m' ? 5 : 11)

  return {
    startEpochMonth: Math.max(startEpochMonth, 0),
    endEpochMonth: lastCompletedEpochMonth,
  }
}

export function formatSalesTrendPeriod(startEpochMonth: number, endEpochMonth: number): string {
  const startDate = EpochMonthService.epochMonthToDate(startEpochMonth)
  const endDate = EpochMonthService.epochMonthToDate(endEpochMonth)
  const startLabel = EpochMonthService.epochMonthToMMM(startEpochMonth)
  const endLabel = EpochMonthService.epochMonthToMMM(endEpochMonth)

  if (startEpochMonth === endEpochMonth) return `${EpochMonthService.epochMonthToMMMM(endEpochMonth)} ${endDate.getFullYear()}`
  if (startDate.getFullYear() === endDate.getFullYear()) return `${startLabel} – ${endLabel} ${endDate.getFullYear()}`
  return `${startLabel} ${startDate.getFullYear()} – ${endLabel} ${endDate.getFullYear()}`
}

export function getSalesTrendSubtext(range: SalesTrendRange): string {
  if (range === 'ytd') return 'How actual sales compared to plan and last year year to date.'
  if (range === '12m') return 'How actual sales compared to plan and last year over the past 12 months.'
  return 'How actual sales compared to plan and last year over the past 6 months.'
}

export function buildSalesTrendSummaryCards({
  rows,
  startEpochMonth,
  endEpochMonth,
}: {
  rows: ClientLevelItem[];
  startEpochMonth: number;
  endEpochMonth: number;
}): SalesTrendSummaryCard[] {
  if (!rows?.length) return []

  const totals = (rows || []).reduce((summary, row) => ({
    salesPlan: summary.salesPlan + (Number(row.salesPlan) || 0),
    actual: summary.actual + (Number(row.salesRm) || 0),
    lastYear: summary.lastYear + (Number(row.salesLy) || 0),
  }), { salesPlan: 0, actual: 0, lastYear: 0 })
  const planVariance = totals.actual - totals.salesPlan
  const lastYearVariance = totals.actual - totals.lastYear
  const period = formatSalesTrendPeriod(startEpochMonth, endEpochMonth)

  return [
    { label: 'Sales Plan', value: PerformanceDashboardService.moneyFormat(totals.salesPlan, true), sub: period, tone: 'text-gray-500' },
    { label: 'Trailing Actual', value: PerformanceDashboardService.moneyFormat(totals.actual, true), sub: `${rows.length} completed month${rows.length === 1 ? '' : 's'}`, tone: 'text-gray-500' },
    { label: 'Variance to Plan', value: formatDelta(planVariance), sub: buildVarianceSubtext(planVariance, 'plan', totals.actual, totals.salesPlan), tone: buildTargetTone(planVariance) },
    { label: 'Last Year (same period)', value: PerformanceDashboardService.moneyFormat(totals.lastYear, true), sub: period, tone: 'text-gray-500' },
    { label: 'Variance to LY', value: formatDelta(lastYearVariance), sub: buildVarianceSubtext(lastYearVariance, 'last year', totals.actual, totals.lastYear), tone: buildTargetTone(lastYearVariance) },
    { label: 'Period', value: period, sub: 'Completed months only', tone: 'text-gray-500' },
  ]
}

export function buildInventoryTrendChartData(monthlyRows: ClientLevelItem[], startEpochMonth?: number, endEpochMonth?: number) {
  return [...(monthlyRows || [])]
    .filter((item) => (
      startEpochMonth !== undefined && endEpochMonth !== undefined
        ? Number(item?.epochMonthSpan) >= Number(startEpochMonth) && Number(item?.epochMonthSpan) <= Number(endEpochMonth)
        : true
    ))
    .sort((a, b) => a.epochMonthSpan - b.epochMonthSpan)
    .map((item) => ({
      date: EpochMonthService.epochMonthToMMM(item.epochMonthSpan),
      actual: Math.round(Number(item.inventoryBOMRM) || 0),
      lastYear: Math.round(Number(item.inventoryBomLy) || 0),
      plan: Math.round(Number(item.inventoryBOMPlan) || 0),
    }))
}

export const buildInventoryTrendChartPoints = buildInventoryTrendChartData

export function buildInventoryTrendSummaryCards({
  rows,
  startEpochMonth,
  endEpochMonth,
}: {
  rows: ClientLevelItem[];
  startEpochMonth?: number;
  endEpochMonth?: number;
}): SalesTrendSummaryCard[] {
  if (!rows?.length) return []

  const filteredRows = rows.filter((row) => (
    startEpochMonth !== undefined && endEpochMonth !== undefined
      ? Number(row?.epochMonthSpan) >= Number(startEpochMonth) && Number(row?.epochMonthSpan) <= Number(endEpochMonth)
      : true
  ))

  const totals = filteredRows.reduce((summary, row) => ({
    plan: summary.plan + (Number(row.inventoryBOMPlan) || 0),
    actual: summary.actual + (Number(row.inventoryBOMRM) || 0),
    lastYear: summary.lastYear + (Number(row.inventoryBomLy) || 0),
  }), { plan: 0, actual: 0, lastYear: 0 })

  const count = filteredRows.length || 1
  const planVariance = totals.actual - totals.plan
  const lastYearVariance = totals.actual - totals.lastYear
  const period = startEpochMonth !== undefined && endEpochMonth !== undefined ? formatSalesTrendPeriod(startEpochMonth, endEpochMonth) : 'Selected Period'

  return [
    { label: 'Inventory Plan', value: PerformanceDashboardService.moneyFormat(totals.plan, true), sub: period, tone: 'text-gray-500' },
    { label: 'Actual Inventory', value: PerformanceDashboardService.moneyFormat(totals.actual, true), sub: `${count} completed month${count === 1 ? '' : 's'}`, tone: 'text-gray-500' },
    { label: 'Variance to Plan', value: formatDelta(planVariance), sub: buildVarianceSubtext(planVariance, 'plan', totals.actual, totals.plan), tone: buildTargetTone(-planVariance) },
    { label: 'Last Year (same period)', value: PerformanceDashboardService.moneyFormat(totals.lastYear, true), sub: period, tone: 'text-gray-500' },
    { label: 'Variance to LY', value: formatDelta(lastYearVariance), sub: buildVarianceSubtext(lastYearVariance, 'last year', totals.actual, totals.lastYear), tone: buildTargetTone(-lastYearVariance) },
  ]
}

export function buildOpenToBuyKpiModel(data: Partial<OpenToBuyKpiData> | null, value: OpenToBuyValue = 'retail') {
  const suffix = value === 'retail' ? 'Retail' : 'Cost'
  const planned = Number(data?.[`planned${suffix}` as keyof OpenToBuyKpiData]) || 0
  const onOrder = Number(data?.[`onOrder${suffix}` as keyof OpenToBuyKpiData]) || 0
  const remaining = Number(data?.[`remaining${suffix}` as keyof OpenToBuyKpiData]) || 0
  const chartMaximum = Math.max(Math.abs(planned), Math.abs(onOrder), Math.abs(remaining), 1)

  return {
    planned,
    onOrder,
    remaining,
    plannedPercent: (Math.abs(planned) / chartMaximum) * 100,
    onOrderPercent: (Math.abs(onOrder) / chartMaximum) * 100,
    remainingPercent: (Math.abs(remaining) / chartMaximum) * 100,
  }
}

export function resolveOpenToBuyKpi({
  data,
  value = 'retail',
  calculationMode,
  currentEpochMonth,
  endingEpochMonth,
  includeCurrentMonthDeviation = false,
  clientRows,
  classRows,
}: {
  data: Partial<OpenToBuyKpiData> | null;
  value?: OpenToBuyValue;
  calculationMode: OpenToBuyCalculationMode;
  currentEpochMonth: number;
  endingEpochMonth: number;
  includeCurrentMonthDeviation?: boolean;
  clientRows?: ClientLevelItem[] | null;
  classRows?: ClassLevelItem[] | null;
}) {
  const model = buildOpenToBuyKpiModel(data, value)

  // The KPI endpoint already returns the legacy-filtered MTD remaining value.
  // Only fall back to the row-level legacy calculation when we explicitly need
  // to layer in the optional current-month deviation adjustment.
  if (calculationMode !== 'mtd' || !includeCurrentMonthDeviation) {
    return model
  }

  const remaining = calculateLegacyClientProgressOpenToBuyRemaining({
    clientRows,
    classRows,
    currentEpochMonth,
    endingEpochMonth,
    value,
    includeCurrentMonthDeviation,
  })
  const chartMaximum = Math.max(Math.abs(model.planned), Math.abs(model.onOrder), Math.abs(remaining), 1)

  return {
    ...model,
    remaining,
    plannedPercent: (Math.abs(model.planned) / chartMaximum) * 100,
    onOrderPercent: (Math.abs(model.onOrder) / chartMaximum) * 100,
    remainingPercent: (Math.abs(remaining) / chartMaximum) * 100,
  }
}

export function calculateLegacyClientProgressOpenToBuyRemaining({
  clientRows,
  classRows,
  currentEpochMonth,
  endingEpochMonth,
  value = 'retail',
  includeCurrentMonthDeviation = false,
}: {
  clientRows: ClientLevelItem[] | null | undefined;
  classRows: ClassLevelItem[] | null | undefined;
  currentEpochMonth: number;
  endingEpochMonth: number;
  value?: OpenToBuyValue;
  includeCurrentMonthDeviation?: boolean;
}) {
  const resolvedClientRows = Array.isArray(clientRows) ? clientRows : []
  const resolvedClassRows = Array.isArray(classRows) ? classRows : []

  if (resolvedClientRows.length === 0) return 0

  const currentMonthTrend = resolvedClientRows
    .filter((row) => Number(row.epochMonthSpan) === currentEpochMonth)
    .reduce((maxTrend, row) => Math.max(maxTrend, Number(row.trend) || 0), 0)

  // Same trended inflow/OTB rule as OTB Recap, Inventory Action and POD (SPS-5709):
  // per class, fall back to updated inflow without a trend, cost = retail x MTD cost complement (or 1).
  const currentMonthRemaining = resolvedClassRows
    .filter((row) => Number(row.epochMonthRm) === currentEpochMonth)
    .reduce((sum, row) => {
      // otbLessTrend = BOM plan NM - inventory - sales + markdowns remaining
      const otbLessTrend = Number(row.otbLessTrend) || 0
      const sales = Number(row.salesRm) || 0
      const salesPlan = Number(row.salesPlan) || 0
      const inflowTrendRetail = currentMonthTrend > 0
        ? otbLessTrend + sales / currentMonthTrend
        : otbLessTrend + sales + Math.max(salesPlan - sales, 0)

      return sum + (value === 'cost'
        ? inflowTrendRetail * ReportService.resolveTrendCostFactor(Number(row.costComplementMtd)) - (Number(row.onOrderCostMTD) || 0)
        : inflowTrendRetail - (Number(row.onOrderRetailMTD) || 0))
    }, 0)

  const futureMonthsRemaining = resolvedClientRows
    .filter((row) => Number(row.epochMonthSpan) > currentEpochMonth && Number(row.epochMonthSpan) <= endingEpochMonth)
    .reduce((sum, row) => sum + (value === 'cost'
      ? (Number(row.otbCostMtd) || 0)
      : (Number(row.otbRetailMtd) || 0)), 0)

  const currentMonthDeviation = includeCurrentMonthDeviation
    ? resolvedClientRows
      .filter((row) => Number(row.epochMonthSpan) === currentEpochMonth)
      .reduce((sum, row) => sum + (value === 'cost'
        ? (Number(row.inventoryDeviationCostRm) || 0)
        : (Number(row.inventoryDeviationRm) || 0)), 0)
    : 0

  return currentMonthRemaining + futureMonthsRemaining + currentMonthDeviation
}

export function buildOpenToBuyIndiePrompt({
  planned,
  onOrder,
  remaining,
  value,
  calculationMode,
  currentEpochMonth,
  endingEpochMonth,
  location,
  category,
  classCode,
  includeCurrentMonthDeviation = false,
}: {
  planned: number;
  onOrder: number;
  remaining: number;
  value: OpenToBuyValue;
  calculationMode: OpenToBuyCalculationMode;
  currentEpochMonth: number;
  endingEpochMonth: number;
  location: string;
  category: string;
  classCode: string;
  includeCurrentMonthDeviation?: boolean;
}) {
  const filters = [
    location ? `location ${location}` : 'all locations',
    category ? `category ${category}` : 'all categories',
    classCode ? `class ${classCode}` : 'all classes',
  ].join(', ')

  return [
    'Help me evaluate my current Open to Buy position and recommend next actions.',
    `Calculation: ${calculationMode.toUpperCase()} ${value}; period: ${EpochMonthService.epochMonthToMMMYY(currentEpochMonth)} through ${EpochMonthService.epochMonthToMMMYY(endingEpochMonth)}; filters: ${filters}.`,
    includeCurrentMonthDeviation && calculationMode === 'mtd'
      ? 'Current month deviation is included in the displayed MTD remaining value.'
      : null,
    `Displayed OTB: Remaining ${PerformanceDashboardService.moneyFormat(remaining, true)}, Planned ${PerformanceDashboardService.moneyFormat(planned, true)}, On Order ${PerformanceDashboardService.moneyFormat(onOrder, true)}.`,
    'Please explain the drivers, identify risks or opportunities, and suggest what I should review in the plan.',
  ].filter(Boolean).join('\n\n')
}

const HUB_TILE_TITLE_CLASS_NAME = 'font-geist !text-[18px] !font-bold !text-[#101828]'

const REPORT_INSIGHT_ROWS: ReportInsightRow[] = [
  {
    id: 'otb-recap',
    title: 'OTB Recap',
    icon: Target,
    iconWrapperClassName: 'bg-[#FFF3E0]',
    iconClassName: 'text-[#C4700C]',
    badgeClassName: 'bg-[#FFF3E0] text-[#C4700C]',
    badgeLabel: 'Recommended',
  },
  {
    id: 'inventory-action',
    title: 'Inventory Action',
    icon: Package,
    iconWrapperClassName: 'bg-[#FEF3F2]',
    iconClassName: 'text-[#D92D20]',
    badgeClassName: 'bg-[#FEF3F2] text-[#D92D20]',
    badgeLabel: 'Recommended',
    openReport: 'inventory-action',
  },
  {
    id: 'progress',
    title: 'Progress',
    icon: TrendingUp,
    iconWrapperClassName: 'bg-[#F0FDF9]',
    iconClassName: 'text-[#0E9384]',
    badgeClassName: 'bg-[#F0FDF9] text-[#0E9384]',
    badgeLabel: 'Recommended',
  },
  {
    id: 'kpi-report',
    title: 'KPI Report',
    icon: BarChart3,
    iconWrapperClassName: 'bg-[#EEEDFE]',
    iconClassName: 'text-[#534AB7]',
    badgeClassName: 'bg-[#EEEDFE] text-[#534AB7]',
    badgeLabel: 'Recommended',
    badgeSparkle: true,
  },
]

const RETAIL_INSIGHTS_BLOG_URL = 'https://www.management-one.com/blog'

function formatPercentValue(value: number | null | undefined, digits = 1) {
  if (!Number.isFinite(Number(value))) return '—'
  return `${Number(value).toFixed(digits)}%`
}

function formatSignedPercentValue(value: number | null | undefined, digits = 1) {
  if (!Number.isFinite(Number(value))) return '—'
  const numericValue = Number(value)
  return `${numericValue >= 0 ? '+' : ''}${numericValue.toFixed(digits)}%`
}

export function summarizeBestPerformers(rows: SellThroughRow[], limit = 3) {
  return rows.slice(0, limit).map((row) => {
    const yoyDelta = (row.compToLyPercent || 0) * -1
    const goalDelta = (row.sellThroughRate || 0) - (row.sellThroughGoal || 0)

    return {
      key: `${row.locationCode || 'all'}-${row.classCode}`,
      label: row.className,
      category: row.category,
      location: row.locationName || row.locationCode || 'All locations',
      salesLabel: PerformanceDashboardService.moneyFormat(row.sales || 0, true),
      sellThroughLabel: formatPercentValue(row.sellThroughRate || 0),
      yoyLabel: `${formatSignedPercentValue(yoyDelta)} vs LY`,
      goalLabel: `${formatSignedPercentValue(goalDelta)} vs goal`,
      goalPositive: goalDelta >= 0,
    }
  })
}

export function summarizeBuyingOpportunities(rows: InventoryHealthRow[], limit = 4) {
  return rows.slice(0, limit).map((row) => {
    const planGap = (row.inventoryPlanRetail || 0) - (row.inventoryRetail || 0)
    const trendGap = (row.inventoryTrendRetail || 0) - (row.inventoryRetail || 0)
    const otbValue = row.otbRetail || 0

    return {
      key: `${row.locationCode || 'all'}-${row.classCode}`,
      label: row.className,
      category: row.category,
      location: row.locationName || row.locationCode || 'All locations',
      otbLabel: PerformanceDashboardService.moneyFormat(otbValue, true),
      salesLabel: PerformanceDashboardService.moneyFormat(row.salesRetail || 0, true),
      onOrderLabel: PerformanceDashboardService.moneyFormat(row.onOrderRetail || 0, true),
      planGapLabel: PerformanceDashboardService.moneyFormat(Math.abs(planGap), true),
      trendGapLabel: PerformanceDashboardService.moneyFormat(Math.abs(trendGap), true),
      status: planGap > 0 ? 'Chase' : 'Watch',
      statusClassName: planGap > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
      planGapPositive: planGap > 0,
      trendGapPositive: trendGap > 0,
    }
  })
}

export function buildAdvisorInsights({
  currentMonth,
  totalOverstocked,
  missedSales,
  slowestSellThroughRows,
  buyingOpportunityRows,
}: {
  currentMonth: ClientLevelItem | null;
  totalOverstocked: number;
  missedSales: number;
  slowestSellThroughRows: SellThroughRow[];
  buyingOpportunityRows: InventoryHealthRow[];
}): AdvisorInsight[] {
  const insights: AdvisorInsight[] = []

  if (missedSales > 0) {
    insights.push({
      title: 'Recover demand',
      detail: `Understock exposure points to ${PerformanceDashboardService.moneyFormat(missedSales, true)} in estimated missed sales that can be chased back into the plan.`,
      toneClassName: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    })
  }

  if (totalOverstocked > 0) {
    insights.push({
      title: 'Release cash',
      detail: `Overstocked classes are holding ${PerformanceDashboardService.moneyFormat(totalOverstocked, true)} above plan and should be reviewed before the next call.`,
      toneClassName: 'border-orange-200 bg-orange-50 text-orange-900',
    })
  }

  if (slowestSellThroughRows[0]) {
    const slowestClass = slowestSellThroughRows[0]
    insights.push({
      title: `Watch ${slowestClass.className}`,
      detail: `${formatPercentValue(slowestClass.sellThroughRate)} sell-through is trailing its goal by ${formatPercentValue(Math.max((slowestClass.sellThroughGoal || 0) - (slowestClass.sellThroughRate || 0), 0))}.`,
      toneClassName: 'border-red-200 bg-red-50 text-red-900',
    })
  }

  if (buyingOpportunityRows[0]) {
    const opportunity = buyingOpportunityRows[0]
    insights.push({
      title: `Chase ${opportunity.className}`,
      detail: `${PerformanceDashboardService.moneyFormat(opportunity.otbRetail || 0, true)} of open-to-buy is available with ${PerformanceDashboardService.moneyFormat(opportunity.onOrderRetail || 0, true)} already on order.`,
      toneClassName: 'border-purple-200 bg-purple-50 text-purple-900',
    })
  }

  if (insights.length === 0 && currentMonth) {
    insights.push({
      title: 'Stable month',
      detail: `Sales are running at ${PerformanceDashboardService.moneyFormat(currentMonth.salesRm || 0, true)} with no major class-level alerts derived from the legacy dashboard feed.`,
      toneClassName: 'border-slate-200 bg-slate-50 text-slate-900',
    })
  }

  return insights.slice(0, 3)
}

export function buildJourneyMilestones({
  currentMonth,
  chartPointCount,
  nextThreeMonthOtb,
  buyingOpportunityRows,
  fastestSellThroughRows,
}: {
  currentMonth: ClientLevelItem | null;
  chartPointCount: number;
  nextThreeMonthOtb: number;
  buyingOpportunityRows: InventoryHealthRow[];
  fastestSellThroughRows: SellThroughRow[];
}): JourneyMilestone[] {
  return [
    {
      label: 'Monthly recap live',
      detail: currentMonth
        ? `${EpochMonthService.epochMonthToMMMYY(currentMonth.epochMonthSpan)} recap is now running on the legacy dashboard feed.`
        : 'Waiting for a client selection.',
      status: currentMonth ? 'live' : 'next',
    },
    {
      label: 'Trend signal connected',
      detail: chartPointCount >= 3
        ? `${chartPointCount} monthly points are available for trend analysis.`
        : 'Need at least three loaded months for a meaningful trend line.',
      status: chartPointCount >= 3 ? 'live' : 'watch',
    },
    {
      label: 'Inventory actions mapped',
      detail: buyingOpportunityRows.length > 0
        ? `${buyingOpportunityRows.length} buying opportunities are ready for follow-up, led by ${buyingOpportunityRows[0].className}.`
        : 'Buying-opportunity ranking will appear once inventory health data is loaded.',
      status: buyingOpportunityRows.length > 0 ? 'live' : 'watch',
    },
    {
      label: 'Best performers surfaced',
      detail: fastestSellThroughRows.length > 0
        ? `${fastestSellThroughRows.length} top sell-through classes are available, anchored by ${fastestSellThroughRows[0].className}.`
        : nextThreeMonthOtb > 0
          ? `Open-to-buy of ${PerformanceDashboardService.moneyFormat(nextThreeMonthOtb, true)} is loaded while performer ranking is still catching up.`
          : 'Performance ranking will appear after the sell-through feed returns data.',
      status: fastestSellThroughRows.length > 0 ? 'live' : nextThreeMonthOtb > 0 ? 'watch' : 'next',
    },
  ]
}

function formatDelta(value: number) {
  const absoluteValue = Math.abs(value)
  const prefix = value >= 0 ? '+' : '-'
  return `${prefix}${PerformanceDashboardService.moneyFormat(absoluteValue, true)}`
}

function formatVariancePercent(delta: number, comparisonValue: number) {
  if (!Number.isFinite(comparisonValue) || comparisonValue <= 0) {
    return null
  }

  const percent = (delta / comparisonValue) * 100

  if (Math.abs(percent) < 0.05) {
    return '0.0%'
  }

  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`
}

function buildVarianceSubtext(delta: number, comparisonLabel: string, actual: number, comparisonValue: number) {
  const baseLabel = delta > 0
    ? `Actual above ${comparisonLabel}`
    : delta < 0
      ? `Actual below ${comparisonLabel}`
      : `Actual matched ${comparisonLabel}`

  const percentLabel = formatVariancePercent(actual - comparisonValue, comparisonValue)

  return percentLabel ? `${baseLabel} · ${percentLabel}` : baseLabel
}

function buildAchievement(current: number, comparisonValue: number) {
  if (!Number.isFinite(comparisonValue) || comparisonValue <= 0) {
    return { value: null, percent: null }
  }

  return {
    value: PerformanceDashboardService.moneyFormat(comparisonValue, true),
    percent: Math.max(0, (current / comparisonValue) * 100),
  }
}

function buildPlanAchievement(current: number, plan: number) {
  const achievement = buildAchievement(current, plan)
  return {
    planValue: achievement.value,
    planAchievementPercent: achievement.percent,
  }
}

function buildLastYearAchievement(current: number, lastYear: number) {
  const achievement = buildAchievement(current, lastYear)
  return {
    lastYearValue: achievement.value,
    lastYearAchievementPercent: achievement.percent,
  }
}

export function buildAchievementBarModel(percent: number): AchievementBarModel {
  const safePercent = Number.isFinite(percent) ? Math.max(0, percent) : 0
  const showOverflowThreshold = safePercent > 100
  const scaleMax = showOverflowThreshold ? 100 + ACHIEVEMENT_BAR_OVERFLOW_VISUAL_CAP : 100
  const thresholdPercent = (100 / scaleMax) * 100
  const overflowPercent = Math.min(Math.max(safePercent - 100, 0), ACHIEVEMENT_BAR_OVERFLOW_VISUAL_CAP)

  return {
    scaleMax,
    fillPercent: showOverflowThreshold
      ? ((100 + overflowPercent) / scaleMax) * 100
      : Math.min((safePercent / scaleMax) * 100, 100),
    thresholdPercent,
    showOverflowThreshold,
  }
}

export function buildMonthlyRecapCards(
  currentMonth: ClientLevelItem | null,
  monthlyRows: ClientLevelItem[],
  range: MonthlyRecapRange = 'MTD',
  comparison: MonthlyRecapComparison = 'vs LY',
): RecapCardModel[] {
  if (!currentMonth) return []

  const metricMonth = range === 'Last month'
    ? monthlyRows.slice(-2, -1)[0] || currentMonth
    : currentMonth
  const trendRows = range === 'Last month'
    ? [metricMonth]
    : monthlyRows.slice(-MONTHLY_RECAP_RANGE_MONTHS[range])
  const buildTrend = (
    currentValue: (row: ClientLevelItem) => number,
    comparisonValue: (row: ClientLevelItem) => number,
  ): RecapTrendPoint[] => trendRows.map((row) => ({
    current: Number(currentValue(row)) || 0,
    comparison: Number(comparisonValue(row)) || 0,
  }))

  const compareToPlan = comparison === 'vs Plan'
  const salesComparison = (row: ClientLevelItem) => compareToPlan ? row.salesPlan : row.salesLy
  const inventoryComparison = (row: ClientLevelItem) => compareToPlan ? row.inventoryBOMPlan : row.inventoryBomLy
  const inflowComparison = (row: ClientLevelItem) => compareToPlan ? row.inflowPlanRetailRm : row.inflowRetailLy
  const markdownComparison = (row: ClientLevelItem) => compareToPlan ? row.markdownsPlan : row.markdownsLy
  const salesDelta = (metricMonth.salesRm || 0) - (salesComparison(metricMonth) || 0)
  const inventoryDelta = (metricMonth.inventoryBOMRM || 0) - (inventoryComparison(metricMonth) || 0)
  const inflowDelta = (metricMonth.inflowRetailRm || 0) - (inflowComparison(metricMonth) || 0)
  const markdownDelta = (metricMonth.markdownsRm || 0) - (markdownComparison(metricMonth) || 0)
  const salesPlanAchievement = buildPlanAchievement(metricMonth.salesRm || 0, metricMonth.salesPlan || 0)
  const inventoryPlanAchievement = buildPlanAchievement(metricMonth.inventoryBOMRM || 0, metricMonth.inventoryBOMPlan || 0)
  const inflowPlanAchievement = buildPlanAchievement(metricMonth.inflowRetailRm || 0, metricMonth.inflowPlanRetailRm || 0)
  const markdownPlanAchievement = buildPlanAchievement(metricMonth.markdownsRm || 0, metricMonth.markdownsPlan || 0)
  const salesLastYearAchievement = buildLastYearAchievement(metricMonth.salesRm || 0, metricMonth.salesLy || 0)
  const inventoryLastYearAchievement = buildLastYearAchievement(metricMonth.inventoryBOMRM || 0, metricMonth.inventoryBomLy || 0)
  const inflowLastYearAchievement = buildLastYearAchievement(metricMonth.inflowRetailRm || 0, metricMonth.inflowRetailLy || 0)
  const markdownLastYearAchievement = buildLastYearAchievement(metricMonth.markdownsRm || 0, metricMonth.markdownsLy || 0)

  return [
    {
      label: 'Sales',
      value: PerformanceDashboardService.moneyFormat(metricMonth.salesRm || 0, true),
      comparison: formatDelta(salesDelta),
      comparisonPositive: salesDelta >= 0,
      trend: buildTrend((row) => row.salesRm, salesComparison),
      trendLabel: null,
      ...salesPlanAchievement,
      ...salesLastYearAchievement,
    },
    {
      label: 'Inventory',
      value: PerformanceDashboardService.moneyFormat(metricMonth.inventoryBOMRM || 0, true),
      comparison: formatDelta(inventoryDelta),
      comparisonPositive: inventoryDelta <= 0,
      trend: buildTrend((row) => row.inventoryBOMRM, inventoryComparison),
      trendLabel: null,
      ...inventoryPlanAchievement,
      ...inventoryLastYearAchievement,
    },
    {
      label: 'Inflow',
      value: PerformanceDashboardService.moneyFormat(metricMonth.inflowRetailRm || 0, true),
      comparison: formatDelta(inflowDelta),
      comparisonPositive: inflowDelta >= 0,
      trend: buildTrend((row) => row.inflowRetailRm, inflowComparison),
      trendLabel: null,
      ...inflowPlanAchievement,
      ...inflowLastYearAchievement,
    },
    {
      label: 'Markdowns',
      value: PerformanceDashboardService.moneyFormat(metricMonth.markdownsRm || 0, true),
      comparison: formatDelta(markdownDelta),
      comparisonPositive: markdownDelta <= 0,
      trend: buildTrend((row) => row.markdownsRm, markdownComparison),
      trendLabel: null,
      ...markdownPlanAchievement,
      ...markdownLastYearAchievement,
    },
  ]
}

export function buildClientProgressMonthlyRecapCards(
  data: ClientProgressRecapData | null,
  range: Extract<MonthlyRecapRange, 'MTD' | 'Last month'> = 'MTD',
  comparison: MonthlyRecapComparison = 'vs LY',
  trendPercent?: number | null,
): RecapCardModel[] {
  if (!data) return []

  const lastMonth = range === 'Last month'
  const compareToPlan = comparison === 'vs Plan'
  const metrics = [
    {
      label: 'Sales',
      current: lastMonth ? data.salesLm : data.salesRm,
      plan: lastMonth ? data.salesPlanLm : data.salesPlanRm,
      lastYear: lastMonth ? data.salesLmLy : data.salesRmLy,
      positiveWhen: (delta: number) => delta >= 0,
    },
    {
      label: 'Inventory',
      current: lastMonth ? data.inventoryBomLm : data.inventoryRm,
      plan: lastMonth ? data.inventoryBomPlanLm : data.inventoryBomPlanNm,
      lastYear: lastMonth ? data.inventoryBomLmLy : data.inventoryRmLy,
      positiveWhen: (delta: number) => delta <= 0,
    },
    {
      label: 'Inflow',
      current: lastMonth ? data.inflowRetailLm : (data.inflowRetail_RM ?? 0),
      plan: lastMonth ? data.inflowPlanRetailLm : data.inflowPlanRetailRm,
      lastYear: lastMonth ? data.inflowRetailLmLy : data.inflowRetailRmLy,
      positiveWhen: (delta: number) => delta >= 0,
    },
    {
      label: 'Markdowns',
      current: lastMonth ? data.markdownsLm : data.markdownsRm,
      plan: lastMonth ? data.markdownsPlanLm : data.markdownsPlanRm,
      lastYear: lastMonth ? data.markdownsLmLy : data.markdownsRmLy,
      positiveWhen: (delta: number) => delta <= 0,
    },
  ]

  return metrics.map((metric) => {
    const current = Number(metric.current) || 0
    const comparisonValue = Number(compareToPlan ? metric.plan : metric.lastYear) || 0
    const delta = current - comparisonValue
    const hasTrend = range === 'MTD' && Number.isFinite(Number(trendPercent)) && Number(trendPercent) > 0
    const trendBasis = (hasTrend && metric.label !== 'Inventory') ? Math.abs(current / Number(trendPercent)) : current
    const planAchievement = buildPlanAchievement(trendBasis, Number(metric.plan) || 0)
    const lastYearAchievement = buildLastYearAchievement(current, Number(metric.lastYear) || 0)
    const showTrendLabel = range === 'MTD' && metric.label === 'Sales' && Number.isFinite(Number(trendPercent)) && Number(trendPercent) !== 0
    const startValue = comparisonValue
    const endValue = current

    return {
      label: metric.label,
      value: PerformanceDashboardService.moneyFormat(current, true),
      comparison: formatDelta(delta),
      comparisonPositive: metric.positiveWhen(delta),
      trend: [
        { current: startValue, comparison: comparisonValue },
        { current: endValue, comparison: comparisonValue },
      ],
      trendLabel: showTrendLabel ? `Trend ${PerformanceDashboardService.moneyFormat(Math.abs(current / Number(trendPercent)), true)}` : null,
      ...planAchievement,
      ...lastYearAchievement,
    }
  })
}

function RecapSparkline({
  trend,
  positive,
}: {
  trend: RecapTrendPoint[]
  positive: boolean
}) {
  if (!trend || trend.length === 0) return null

  const points = trend.length === 1
    ? [{ current: trend[0].comparison, comparison: trend[0].comparison }, trend[0]]
    : trend

  const strokeColor = '#7F56D9'
  const currentValues = points.map((p) => p.current)
  const compValues = points.map((p) => p.comparison)
  const allValues = [...currentValues, ...compValues]

  let minVal = Math.min(...allValues)
  let maxVal = Math.max(...allValues)

  if (minVal === maxVal) {
    minVal = minVal * 0.9
    maxVal = maxVal * 1.1
    if (minVal === 0 && maxVal === 0) {
      maxVal = 100
    }
  }

  const range = maxVal - minVal || 1
  const width = 240
  const height = 36

  const getSvgY = (val: number) => {
    const normalized = (val - minVal) / range
    return height - 4 - normalized * (height - 8)
  }

  const currentPathD = points
    .map((p, index) => {
      const x = (index / (points.length - 1)) * width
      const y = getSvgY(p.current)
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')

  const compPathD = points
    .map((p, index) => {
      const x = (index / (points.length - 1)) * width
      const y = getSvgY(p.comparison)
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <div className="mt-3 space-y-1" data-testid="recap-sparkline-container">
      <div className="h-[36px] w-full overflow-hidden">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full overflow-visible" preserveAspectRatio="none">
          <path
            d={compPathD}
            fill="none"
            stroke="#D0D5DD"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <path
            d={currentPathD}
            fill="none"
            stroke={strokeColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="flex items-center justify-between text-[10px] font-medium text-[#667085]">
        <span>Month to date</span>
        <span>Now</span>
      </div>
    </div>
  )
}

function RecapAchievementBar({ label, percent, descriptor }: { label: string; percent: number; descriptor: string }) {
  const roundedPercent = Math.round(percent)
  const { scaleMax, fillPercent, thresholdPercent, showOverflowThreshold } = buildAchievementBarModel(percent)
  const percentText = descriptor.startsWith('%') ? `${roundedPercent}${descriptor}` : `${roundedPercent}% ${descriptor}`
  const sanitizedDescriptor = descriptor.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase()

  return (
    <div
      aria-label={`${label}: ${percentText}`}
      aria-valuemax={scaleMax}
      aria-valuemin={0}
      aria-valuenow={roundedPercent}
      aria-valuetext={percentText}
      className="relative h-[14px] w-full"
      role="progressbar"
    >
      <div className="absolute left-0 top-1/2 h-[6px] w-full -translate-y-1/2 overflow-hidden rounded-full bg-[#D9D9D9]">
        <div
          className="h-full rounded-full bg-[#7F56D9]"
          data-testid={`${label.toLowerCase()}-${sanitizedDescriptor}-fill`}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
      {showOverflowThreshold && (
        <div
          aria-hidden="true"
          className="absolute top-1/2 h-[14px] w-[2px] -translate-x-1/2 -translate-y-1/2 bg-[#101828]"
          data-testid={`${label.toLowerCase()}-${sanitizedDescriptor}-threshold`}
          style={{ left: `${thresholdPercent}%` }}
        />
      )}
    </div>
  )
}

function resolveMonthlyRecapTooltipKey(label: string): KpiTooltipKey | null {
  switch (label) {
    case 'Sales':
      return 'sales'
    case 'Inventory':
      return 'inventoryAtRetail'
    case 'Inflow':
      return 'inflow'
    case 'Markdowns':
      return 'markdowns'
    default:
      return null
  }
}

function buildTargetTone(delta: number) {
  if (delta > 0) return 'text-emerald-600'
  if (delta < 0) return 'text-red-600'
  return 'text-gray-500'
}

export function getPerformanceGaugeScore(currentMonth: ClientLevelItem | null) {
  if (!currentMonth) return 0

  const plan = Number(currentMonth.salesPlan) || 0
  const actual = Number(currentMonth.salesRm) || 0

  if (plan <= 0) return 0

  return Math.round((actual / plan) * 100)
}

export function buildSalesTrendChartData(monthlyRows: ClientLevelItem[], startEpochMonth: number, endEpochMonth: number) {
  return [...(monthlyRows || [])]
    .filter((item) => (
      Number(item?.epochMonthSpan) >= Number(startEpochMonth)
      && Number(item?.epochMonthSpan) <= Number(endEpochMonth)
    ))
    .sort((a, b) => a.epochMonthSpan - b.epochMonthSpan)
    .map((item) => ({
      date: EpochMonthService.epochMonthToMMM(item.epochMonthSpan),
      actual: Math.round(Number(item.salesRm) || 0),
      current: Math.round(Number(item.salesRm) || 0),
      lastYear: Math.round(Number(item.salesLy) || 0),
      plan: Math.round(Number(item.salesPlan) || 0),
    }))
}

function buildSummaryTone(isPositive: boolean) {
  return isPositive ? 'text-emerald-600' : 'text-red-600'
}

export function buildPerformanceGaugeModel({
  currentMonth,
  previousMonth,
}: {
  currentMonth: ClientLevelItem | null;
  previousMonth: ClientLevelItem | null;
}) {
  if (!currentMonth) return null

  const score = getPerformanceGaugeScore(currentMonth)
  const plan = Number(currentMonth.salesPlan) || 0
  const actual = Number(currentMonth.salesRm) || 0
  const runRateDivisor = Number(currentMonth.trend) || 0
  const runRate = runRateDivisor > 0 ? actual / runRateDivisor : 0
  const previousActual = Number(previousMonth?.salesRm) || 0
  const delta = actual - plan
  const compareDelta = previousMonth ? actual - previousActual : null

  return {
    score,
    statusLabel: delta >= 0 ? 'Ahead of plan' : 'Behind plan',
    statusClassName: delta >= 0 ? 'text-emerald-600' : 'text-red-600',
    targetLabel: PerformanceDashboardService.moneyFormat(plan, true),
    actualLabel: PerformanceDashboardService.moneyFormat(actual, true),
    runRateLabel: PerformanceDashboardService.moneyFormat(runRate, true),
    compareLabel: PerformanceDashboardService.moneyFormat(previousActual, true),
    deltaLabel: `${formatDelta(delta)} vs plan`,
    compareDeltaLabel: compareDelta === null ? '— vs last month' : `${formatDelta(compareDelta)} vs last month`,
    attainmentLabel: `${score}% to plan`,
  }
}

export function buildPerformanceGaugeLabelModel({
  currentMonth,
  previousMonth,
}: {
  currentMonth: ClientLevelItem | null;
  previousMonth: ClientLevelItem | null;
}) {
  if (!currentMonth) return null

  const currentScore = getPerformanceGaugeScore(currentMonth)
  const previousScore = previousMonth ? getPerformanceGaugeScore(previousMonth) : null
  const variance = previousScore === null ? null : currentScore - previousScore
  const monthLabel = `${EpochMonthService.epochMonthToMMMM(currentMonth.epochMonthSpan)} ${EpochMonthService.epochMonthToDate(currentMonth.epochMonthSpan).getFullYear()}`

  return {
    varianceLabel: variance === null
      ? '— pts vs last month'
      : `${variance >= 0 ? '+' : ''}${variance} pts vs last month`,
    monthLabel: `Health Score · ${monthLabel}`,
    varianceToneClassName: variance === null
      ? 'text-gray-500'
      : buildSummaryTone(variance >= 0),
  }
}

export function buildVerticalAverageLabelModel({
  categories,
}: {
  categories: CategoryLevelItem[] | null;
}) {
  const label = 'Coming soon'

  const rows = (categories || []).filter((item) => Number(item.salesRm) > 0 || Number(item.salesSumLm) > 0)

  const totals = rows.reduce((acc, item) => {
    const baseline = Number(item.salesSumLm) || Number(item.inventoryBomAvgLm) || 0
    const actual = Number(item.salesRm) || 0

    if (!baseline) {
      return acc
    }

    acc.actual += actual
    acc.baseline += baseline
    return acc
  }, { actual: 0, baseline: 0 })

  const value = totals.baseline > 0
    ? Math.min(Math.max(Math.round((totals.actual / totals.baseline) * 100), 0), 100)
    : null

  return {
    label,
    value,
    valueLabel: 'Coming soon',
    displayLabel: 'Coming soon',
  }
}

function formatNotificationDateParts(value?: string | null) {
  if (!value) return null
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return null

  return {
    month: parsedDate.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
    day: parsedDate.toLocaleString('en-US', { day: 'numeric', timeZone: 'UTC' }),
  }
}

function sanitizeNotificationHtml(value?: string | null) {
  return DOMPurify.sanitize(value || '', {
    ALLOWED_TAGS: ['a', 'b', 'strong', 'em', 'i', 'u', 'br', 'p', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  })
}

export function resolveMonthlyRecapTrendPercent(
  trend: number | null | undefined,
  epochMonthSpan: number | null | undefined,
  today: Date = new Date(),
) {
  const numericTrend = Number(trend)
  if (Number.isFinite(numericTrend) && numericTrend > 0) return numericTrend

  if (!Number.isFinite(Number(epochMonthSpan))) return null

  const currentEpochMonth = EpochMonthService.dateToEpochMonth(today)
  const targetEpochMonth = Number(epochMonthSpan)

  if (targetEpochMonth < currentEpochMonth) return 1
  if (targetEpochMonth > currentEpochMonth) return 0

  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  if (!daysInMonth) return null

  return Math.min(Math.max(today.getDate() / daysInMonth, 0), 1)
}

export default function ClientDashboardV3Home() {
  const currentClientContext = CurrentClientProvider.useClient() as any
  const authContext = AuthContext.useAuth() as any
  const globalTip = GlobalTip.useMessage() as any
  const currentClient = currentClientContext?.client
  const resolvedClientCode = currentClient?.ClientCode || ''
  const resolvedClientId = currentClient?.Id
  const [clientLevelData, setClientLevelData] = React.useState<ClientLevelItem[] | null>(null)
  const [clientProgressRecapData, setClientProgressRecapData] = React.useState<ClientProgressRecapData | null>(null)
  const [categoryLevelData, setCategoryLevelData] = React.useState<CategoryLevelItem[] | null>(null)
  const [classLevelData, setClassLevelData] = React.useState<ClassLevelItem[] | null>(null)
  const [reportCatalogCounts, setReportCatalogCounts] = React.useState<{ total: number; isAvailable: boolean } | null>(null)
  const [dashboardNotifications, setDashboardNotifications] = React.useState<Record<'advisor' | 'journey' | 'event', DashboardNotification[]>>({ advisor: [], journey: [], event: [] })
  const [dashboardNotificationsLoading, setDashboardNotificationsLoading] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  // Vertical average + Ask Indie prompt reported by the Performance tile's health score.
  const [healthSummary, setHealthSummary] = React.useState<HealthScoreSummaryForHost | null>(null)
  const [monthlyRecapRange, setMonthlyRecapRange] = React.useState<MonthlyRecapRange>('MTD')
  const [monthlyRecapComparison, setMonthlyRecapComparison] = React.useState<MonthlyRecapComparison>('vs Plan')
  const [salesTrendRange, setSalesTrendRange] = React.useState<SalesTrendRange>('6m')
  const [salesAndInventoryView, setSalesAndInventoryView] = React.useState<SalesAndInventoryView>('sales')
  const [salesTrendData, setSalesTrendData] = React.useState<ClientLevelItem[]>([])
  const [locationOptions, setLocationOptions] = React.useState<DashboardFilterOption[]>([])
  const [categoryOptions, setCategoryOptions] = React.useState<DashboardFilterOption[]>([])
  const [classOptions, setClassOptions] = React.useState<DashboardFilterOption[]>([])
  const [dashboardFilters, setDashboardFilters] = React.useState(() => createEmptyDashboardFilters())
  const [openToBuyData, setOpenToBuyData] = React.useState<OpenToBuyKpiData | null>(null)
  const [openToBuyLoading, setOpenToBuyLoading] = React.useState(false)
  const [openToBuyError, setOpenToBuyError] = React.useState('')
  const [openToBuyFiltersOpen, setOpenToBuyFiltersOpen] = React.useState(false)
  const [openToBuyValue, setOpenToBuyValue] = React.useState<OpenToBuyValue>('retail')
  const [openToBuyCalculationMode, setOpenToBuyCalculationMode] = React.useState<OpenToBuyCalculationMode>('mtd')
  const [openToBuyIncludeCurrentMonthDeviation, setOpenToBuyIncludeCurrentMonthDeviation] = React.useState(false)
  const [openToBuyEndingPeriod, setOpenToBuyEndingPeriod] = React.useState('')
  const [isSalesDynamicTrendModalOpen, setSalesDynamicTrendModalOpen] = React.useState(false)
  const [openToBuyLocation, setOpenToBuyLocation] = React.useState('')
  const [openToBuyCategory, setOpenToBuyCategory] = React.useState('')
  const [openToBuyClass, setOpenToBuyClass] = React.useState('')
  const [draftOpenToBuyLocation, setDraftOpenToBuyLocation] = React.useState('')
  const [draftOpenToBuyCategory, setDraftOpenToBuyCategory] = React.useState('')
  const [draftOpenToBuyClass, setDraftOpenToBuyClass] = React.useState('')
  const [openToBuyScopedClientRows, setOpenToBuyScopedClientRows] = React.useState<ClientLevelItem[] | null>(null)
  const [openToBuyScopedClassRows, setOpenToBuyScopedClassRows] = React.useState<ClassLevelItem[] | null>(null)
  const openToBuyControlsRef = React.useRef<HTMLDivElement>(null)

  const baseEpochMonth = EpochMonthService.getTodayEpochMonth()
  const epochMonth = baseEpochMonth
  const lastCompletedEpochMonth = Math.max(baseEpochMonth - 1, 0)
  const salesTrendPeriod = React.useMemo(
    () => resolveSalesTrendPeriod(salesTrendRange, lastCompletedEpochMonth),
    [lastCompletedEpochMonth, salesTrendRange],
  )
  const compareEpochMonth = epochMonth - 1
  const dashboardFilterParams = React.useMemo(() => buildDashboardFilterRequestParams(dashboardFilters), [dashboardFilters])
  const dashboardFilterKey = React.useMemo(() => serializeDashboardFilters(dashboardFilters), [dashboardFilters])
  const selectedLocations = dashboardFilterParams.locations
  const selectedCategories = dashboardFilterParams.categories
  const selectedClasses = dashboardFilterParams.classes

  const endingPeriodOptions = React.useMemo(() => Array.from({ length: 13 }, (_, index) => {
    const value = baseEpochMonth + index
    return { value: String(value), label: EpochMonthService.epochMonthToMMMM(value) }
  }), [baseEpochMonth])
  const openToBuyEndingEpochMonth = resolveDashboardEpochMonth(baseEpochMonth, openToBuyEndingPeriod)

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setLocationOptions([])
      setCategoryOptions([])
      setClassOptions([])
      return
    }

    let cancelled = false
    PerformanceDashboardService.getDropdownOptions({
      clientId: currentClient.Id,
      epochMonth,
    }).then((data) => {
      if (cancelled) return
      setLocationOptions(Array.isArray(data?.locations) ? data.locations : [])
      setCategoryOptions(Array.isArray(data?.categories) ? data.categories : [])
      setClassOptions(Array.isArray(data?.classes) ? data.classes : [])
    }).catch((error) => {
      if (!cancelled) console.error('Failed to fetch dashboard filter options', error)
    })

    return () => {
      cancelled = true
    }
  }, [currentClient?.Id, epochMonth])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setReportCatalogCounts(null)
      return
    }

    let cancelled = false
    setReportCatalogCounts(null)

    ReportCatalogService.getDefinedReportCounts({
      clientId: currentClient.Id,
      // Reports.js uses activeClientId for this legacy `client_code` API parameter.
      clientCode: String(currentClient.Id),
      userId: authContext?.user?.id,
    }).then((counts) => {
      if (!cancelled) setReportCatalogCounts(counts)
    }).catch((error) => {
      if (!cancelled) {
        console.error('Failed to fetch defined report counts', error)
        setReportCatalogCounts({ total: 0, isAvailable: false })
      }
    })

    return () => {
      cancelled = true
    }
  }, [authContext?.user?.id, currentClient?.Id])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setDashboardNotifications({ advisor: [], journey: [], event: [] })
      return undefined
    }

    let cancelled = false
    setDashboardNotificationsLoading(true)
    ClientDashboardNotificationService.listForClient(currentClient.Id)
      .then((items) => {
        if (!cancelled) {
          setDashboardNotifications({
            advisor: Array.isArray(items?.advisor) ? items.advisor : [],
            journey: Array.isArray(items?.journey) ? items.journey : [],
            event: Array.isArray(items?.event) ? items.event : [],
          })
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load client dashboard notifications', error)
          setDashboardNotifications({ advisor: [], journey: [], event: [] })
        }
      })
      .finally(() => {
        if (!cancelled) setDashboardNotificationsLoading(false)
      })

    return () => { cancelled = true }
  }, [currentClient?.Id])

  React.useEffect(() => {
    if (!openToBuyFiltersOpen) return

    const dismissFilters = (event: MouseEvent) => {
      if (!openToBuyControlsRef.current?.contains(event.target as Node)) setOpenToBuyFiltersOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenToBuyFiltersOpen(false)
    }

    document.addEventListener('mousedown', dismissFilters)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', dismissFilters)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [openToBuyFiltersOpen])

  React.useEffect(() => {
    setDashboardFilters(createEmptyDashboardFilters())
    setOpenToBuyEndingPeriod('')
    setOpenToBuyLocation('')
    setOpenToBuyCategory('')
    setOpenToBuyClass('')
    setOpenToBuyIncludeCurrentMonthDeviation(false)
    setOpenToBuyFiltersOpen(false)
  }, [currentClient?.Id])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setClientLevelData(null)
      setCategoryLevelData(null)
      setClassLevelData(null)
      setLoading(false)
      return
    }

    setLoading(true)
    PerformanceDashboardService.getSimpleDashboardDataForClient({
      clientId: currentClient.Id,
      epochMonth,
      compareEpochMonth,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
    }).then((data) => {
      setClientLevelData(Array.isArray(data?.client) ? data.client : [])
      setCategoryLevelData(Array.isArray(data?.categories) ? data.categories : [])
      setClassLevelData(Array.isArray(data?.classes) ? data.classes : [])
      setLoading(false)
    }).catch((error) => {
      console.error(error)
      globalTip.show('Failed to fetch dashboard data')
      setClientLevelData([])
      setCategoryLevelData([])
      setClassLevelData([])
      setLoading(false)
    })
  }, [compareEpochMonth, currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude, epochMonth])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setSalesTrendData([])
      return
    }

    let cancelled = false
    PerformanceDashboardService.getPerformanceDashboardKeyMetricTrendData({
      clientId: currentClient.Id,
      startEpochMonth: salesTrendPeriod.startEpochMonth,
      endEpochMonth: salesTrendPeriod.endEpochMonth,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
    }).then((data) => {
      if (!cancelled) setSalesTrendData(Array.isArray(data) ? data as ClientLevelItem[] : [])
    }).catch((error) => {
      if (!cancelled) {
        console.error('Failed to fetch completed-month sales trend data', error)
        setSalesTrendData([])
      }
    })

    return () => { cancelled = true }
  }, [
    currentClient?.Id,
    dashboardFilterKey,
    dashboardFilterParams.categoryExclude,
    dashboardFilterParams.classExclude,
    dashboardFilterParams.locationExclude,
    salesTrendPeriod.endEpochMonth,
    salesTrendPeriod.startEpochMonth,
  ])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setClientProgressRecapData(null)
      return
    }

    let cancelled = false
    PerformanceDashboardService.getMonthlyRecapData({
      clientId: currentClient.Id,
      epochMonth: baseEpochMonth,
      locations: selectedLocations,
      categories: selectedCategories,
      classes: selectedClasses,
      locationExclude: dashboardFilterParams.locationExclude,
      categoryExclude: dashboardFilterParams.categoryExclude,
      classExclude: dashboardFilterParams.classExclude,
    }).then((data) => {
      if (!cancelled) setClientProgressRecapData(data || null)
    }).catch((error) => {
      console.error('Failed to fetch Client Progress Monthly Recap data', error)
      if (!cancelled) setClientProgressRecapData(null)
    })

    return () => { cancelled = true }
  }, [baseEpochMonth, currentClient?.Id, dashboardFilterKey, dashboardFilterParams.categoryExclude, dashboardFilterParams.classExclude, dashboardFilterParams.locationExclude])

  React.useEffect(() => {
    if (!currentClient?.Id) {
      setOpenToBuyData(null)
      setOpenToBuyError('')
      return
    }

    let cancelled = false
    setOpenToBuyLoading(true)
    setOpenToBuyError('')
    PerformanceDashboardService.getOpenToBuyKpiData({
      clientId: currentClient.Id,
      currentEpochMonth: baseEpochMonth,
      endingEpochMonth: openToBuyEndingEpochMonth,
      calculationMode: openToBuyCalculationMode,
      locations: openToBuyLocation ? [openToBuyLocation] : [],
      categories: openToBuyCategory ? [openToBuyCategory] : [],
      classes: openToBuyClass ? [openToBuyClass] : [],
    }).then((data) => {
      if (!cancelled) setOpenToBuyData(data || null)
    }).catch((error) => {
      console.error('Failed to load open-to-buy data', error)
      if (!cancelled) {
        setOpenToBuyData(null)
        setOpenToBuyError('Open-to-buy data is unavailable. Try again shortly.')
      }
    }).finally(() => {
      if (!cancelled) setOpenToBuyLoading(false)
    })

    return () => { cancelled = true }
  }, [baseEpochMonth, currentClient?.Id, openToBuyCalculationMode, openToBuyCategory, openToBuyClass, openToBuyEndingEpochMonth, openToBuyLocation])

  React.useEffect(() => {
    if (!currentClient?.Id || openToBuyCalculationMode !== 'mtd' || !openToBuyIncludeCurrentMonthDeviation) {
      setOpenToBuyScopedClientRows(null)
      setOpenToBuyScopedClassRows(null)
      return
    }

    let cancelled = false
    PerformanceDashboardService.getSimpleDashboardDataForClient({
      clientId: currentClient.Id,
      epochMonth: baseEpochMonth,
      compareEpochMonth,
      locations: openToBuyLocation ? [openToBuyLocation] : [],
      categories: openToBuyCategory ? [openToBuyCategory] : [],
      classes: openToBuyClass ? [openToBuyClass] : [],
    }).then((data) => {
      if (cancelled) return
      setOpenToBuyScopedClientRows(Array.isArray(data?.client) ? data.client : [])
      setOpenToBuyScopedClassRows(Array.isArray(data?.classes) ? data.classes : [])
    }).catch((error) => {
      console.error('Failed to load open-to-buy scoped rows', error)
      if (!cancelled) {
        setOpenToBuyScopedClientRows([])
        setOpenToBuyScopedClassRows([])
      }
    })

    return () => { cancelled = true }
  }, [baseEpochMonth, compareEpochMonth, currentClient?.Id, openToBuyCalculationMode, openToBuyCategory, openToBuyClass, openToBuyIncludeCurrentMonthDeviation, openToBuyLocation])

  const derived = React.useMemo(() => {
    const monthlyRows = [...(clientLevelData || [])]
      .sort((a, b) => a.epochMonthSpan - b.epochMonthSpan)

    const currentMonth = monthlyRows.find((item) => item.epochMonthSpan === epochMonth)
      || monthlyRows[monthlyRows.length - 1]
      || null

    const previousMonth = monthlyRows.find((item) => item.epochMonthSpan === compareEpochMonth)
      || monthlyRows[monthlyRows.length - 2]
      || null

    const currentCategories = (categoryLevelData || []).filter((item) => item.epochMonthRM === (currentMonth?.epochMonthSpan ?? epochMonth))
    const overstockedClasses = currentCategories.filter((item) => item.inventoryBomRm > item.inventoryBomPlan && item.inventoryBomPlan !== 0)
    const understockedClasses = currentCategories.filter((item) => item.inventoryBomRm < item.inventoryBomPlan && item.inventoryBomPlan !== 0)

    const totalOverstocked = Math.round(overstockedClasses.reduce((acc, item) => acc + (item.inventoryBomRm - item.inventoryBomPlan), 0))
    const totalUnderstocked = Math.round(understockedClasses.reduce((acc, item) => acc + (item.inventoryBomPlan - item.inventoryBomRm), 0))
    const understockSales = understockedClasses.reduce((acc, item) => acc + item.salesRm, 0)
    const understockInventory = understockedClasses.reduce((acc, item) => acc + item.inventoryBomRm, 0)
    const understockPlan = understockedClasses.reduce((acc, item) => acc + item.inventoryBomPlan, 0)
    const missedSales = Math.round(PerformanceDashboardService.calculateMissedSales(understockPlan, understockInventory, understockSales))
    const verticalAverage = buildVerticalAverageLabelModel({
      categories: currentCategories,
    })

    return {
      monthlyRows,
      currentMonth,
      previousMonth,
      totalOverstocked,
      totalUnderstocked,
      missedSales,
      currentCategories,
      verticalAverage,
    }
  }, [categoryLevelData, clientLevelData, compareEpochMonth, epochMonth])

  const currentMonth = derived.currentMonth
  const previousMonth = derived.previousMonth
  const [customTrendPercentInput, setCustomTrendPercentInput] = React.useState<string | null>(null)

  const defaultTrendPercent = React.useMemo(() => (
    resolveMonthlyRecapTrendPercent(currentMonth?.trend, currentMonth?.epochMonthSpan)
  ), [currentMonth?.epochMonthSpan, currentMonth?.trend])

  const activeTrendPercent = React.useMemo(() => {
    if (customTrendPercentInput !== null) {
      const parsed = parseFloat(customTrendPercentInput)
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed / 100
      }
    }
    return defaultTrendPercent
  }, [customTrendPercentInput, defaultTrendPercent])

  const monthlyRecapTrendPercentLabel = React.useMemo(() => {
    if (customTrendPercentInput !== null) {
      return customTrendPercentInput
    }
    return activeTrendPercent === null ? '—' : `${(activeTrendPercent * 100).toFixed(1)}%`
  }, [activeTrendPercent, customTrendPercentInput])

  const recapCards = React.useMemo(() => {
    if (monthlyRecapRange === 'MTD' || monthlyRecapRange === 'Last month') {
      if (monthlyRecapRange === 'MTD' && currentMonth && clientProgressRecapData) {
        const salesValue = Number(clientProgressRecapData?.salesRm) || 0
        const trendPercent = Number(activeTrendPercent)
        const derivedTrendValue = Number.isFinite(trendPercent) && trendPercent !== 0
          ? salesValue / trendPercent
          : null

        console.debug('[ClientDashboardV3Home] MTD sales trend inputs', {
          epochMonth: currentMonth?.epochMonthSpan ?? null,
          salesRm: salesValue,
          trendPercent: Number.isFinite(trendPercent) ? trendPercent : null,
          derivedTrendValue,
        })
      }

      return buildClientProgressMonthlyRecapCards(clientProgressRecapData, monthlyRecapRange, monthlyRecapComparison, activeTrendPercent)
    }
    return buildMonthlyRecapCards(currentMonth, derived.monthlyRows, monthlyRecapRange, monthlyRecapComparison)
  }, [activeTrendPercent, clientProgressRecapData, currentMonth, derived.monthlyRows, monthlyRecapComparison, monthlyRecapRange])

  const salesTrendChartData = React.useMemo(
    () => buildSalesTrendChartData(salesTrendData, salesTrendPeriod.startEpochMonth, salesTrendPeriod.endEpochMonth),
    [salesTrendData, salesTrendPeriod.endEpochMonth, salesTrendPeriod.startEpochMonth],
  )
  const targetStats = React.useMemo(
    () => buildSalesTrendSummaryCards({
      rows: salesTrendData,
      startEpochMonth: salesTrendPeriod.startEpochMonth,
      endEpochMonth: salesTrendPeriod.endEpochMonth,
    }),
    [salesTrendData, salesTrendPeriod.endEpochMonth, salesTrendPeriod.startEpochMonth],
  )

  const reportsPanel = React.useMemo(() => {
    if (!currentMonth) return null

    return {
      total: reportCatalogCounts?.isAvailable ? reportCatalogCounts.total : null,
      href: resolvedClientCode && resolvedClientId
        ? buildV3ClientDashboardReportsPath(resolvedClientCode, resolvedClientId)
        : null,
    }
  }, [currentMonth, reportCatalogCounts, resolvedClientCode, resolvedClientId])

  const yearsTogether = React.useMemo(() => {
    const activeDateStr =
      currentClient?.ActiveDate ||
      currentClient?.activeDate ||
      currentClient?.CreatedDate ||
      currentClient?.created_at ||
      currentClient?.StartDate ||
      currentClient?.start_date
    if (!activeDateStr) return 1
    const activeDate = new Date(activeDateStr)
    if (isNaN(activeDate.getTime())) return 1
    const now = new Date()
    let years = now.getFullYear() - activeDate.getFullYear()
    const monthDiff = now.getMonth() - activeDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < activeDate.getDate())) {
      years--
    }
    return Math.max(years, 1)
  }, [currentClient?.ActiveDate, currentClient?.activeDate, currentClient?.CreatedDate, currentClient?.created_at, currentClient?.StartDate, currentClient?.start_date])

  const m1JourneyDateRange = React.useMemo(() => {
    const activeDateStr =
      currentClient?.ActiveDate ||
      currentClient?.activeDate ||
      currentClient?.CreatedDate ||
      currentClient?.created_at ||
      currentClient?.StartDate ||
      currentClient?.start_date
    if (!activeDateStr) return 'June 2023 — present'
    const activeDate = new Date(activeDateStr)
    if (isNaN(activeDate.getTime())) return 'June 2023 — present'
    const startMonth = activeDate.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
    const startYear = activeDate.getUTCFullYear()
    return `${startMonth} ${startYear} — present`
  }, [currentClient?.ActiveDate, currentClient?.activeDate, currentClient?.CreatedDate, currentClient?.created_at, currentClient?.StartDate, currentClient?.start_date])

  const openToBuyKpi = React.useMemo(() => resolveOpenToBuyKpi({
    data: openToBuyData,
    value: openToBuyValue,
    calculationMode: openToBuyCalculationMode,
    currentEpochMonth: baseEpochMonth,
    endingEpochMonth: openToBuyEndingEpochMonth,
    includeCurrentMonthDeviation: openToBuyIncludeCurrentMonthDeviation,
    clientRows: openToBuyScopedClientRows,
    classRows: openToBuyScopedClassRows,
  }), [baseEpochMonth, openToBuyCalculationMode, openToBuyData, openToBuyEndingEpochMonth, openToBuyIncludeCurrentMonthDeviation, openToBuyScopedClassRows, openToBuyScopedClientRows, openToBuyValue])


  const openToBuyIndiePrompt = React.useMemo(() => buildOpenToBuyIndiePrompt({
    ...openToBuyKpi,
    value: openToBuyValue,
    calculationMode: openToBuyCalculationMode,
    currentEpochMonth: baseEpochMonth,
    endingEpochMonth: openToBuyEndingEpochMonth,
    location: openToBuyLocation,
    category: openToBuyCategory,
    classCode: openToBuyClass,
    includeCurrentMonthDeviation: openToBuyIncludeCurrentMonthDeviation,
  }), [baseEpochMonth, openToBuyCalculationMode, openToBuyClass, openToBuyCategory, openToBuyEndingEpochMonth, openToBuyIncludeCurrentMonthDeviation, openToBuyKpi, openToBuyLocation, openToBuyValue])

  const hasActiveDashboardFilters = Boolean(selectedLocations?.length || selectedCategories?.length || selectedClasses?.length)
  const isSalesDynamicTrendEnabled = React.useMemo(() => {
    const numericPointOfSaleId = Number(currentClient?.PointOfSaleId)
    return Number.isFinite(numericPointOfSaleId) && SALES_DYNAMIC_TREND_ELIGIBLE_POS_IDS.has(numericPointOfSaleId)
  }, [currentClient?.PointOfSaleId])
  const salesDynamicTrendLocationOptions = React.useMemo(() => {
    const allLocations = { value: 'all', label: 'All locations' }
    const mapped = (locationOptions || []).map((option) => ({
      value: option.code,
      label: option.label || (option.code && option.name ? `${option.code} - ${option.name}` : option.name || option.code),
    }))
    return [allLocations, ...mapped]
  }, [locationOptions])
  const salesDynamicTrendMonthLabel = React.useMemo(() => {
    const date = EpochMonthService.epochMonthToDate(baseEpochMonth)
    return `${EpochMonthService.epochMonthToMMMM(baseEpochMonth)} ${date.getFullYear()}`
  }, [baseEpochMonth])

  const inventoryTrendChartPoints = React.useMemo(() => {
    return buildInventoryTrendChartData(salesTrendData, salesTrendPeriod.startEpochMonth, salesTrendPeriod.endEpochMonth)
  }, [salesTrendData, salesTrendPeriod.startEpochMonth, salesTrendPeriod.endEpochMonth])

  const inventoryTrendSummaryCards = React.useMemo(() => {
    return buildInventoryTrendSummaryCards({
      rows: salesTrendData,
      startEpochMonth: salesTrendPeriod.startEpochMonth,
      endEpochMonth: salesTrendPeriod.endEpochMonth,
    })
  }, [salesTrendData, salesTrendPeriod.startEpochMonth, salesTrendPeriod.endEpochMonth])
  const trackDashboardInteraction = React.useCallback((eventName: string, payload: Record<string, unknown>) => {
    trackEvent(eventName, {
      clientCode: resolvedClientCode || 'none',
      ...payload,
    })
  }, [resolvedClientCode])
  const handleMonthlyRecapRangeChange = React.useCallback((range: MonthlyRecapRange) => {
    setMonthlyRecapRange(range)
    trackDashboardInteraction('V3 Performance Event', {
      action: 'Monthly Recap Range Changed',
      range,
    })
  }, [trackDashboardInteraction])
  const handleMonthlyRecapComparisonChange = React.useCallback((comparison: MonthlyRecapComparison) => {
    setMonthlyRecapComparison(comparison)
    trackDashboardInteraction('V3 Performance Event', {
      action: 'Monthly Recap Comparison Changed',
      comparison,
    })
  }, [trackDashboardInteraction])
  const handleSalesTrendRangeChange = React.useCallback((range: SalesTrendRange) => {
    setSalesTrendRange(range)
    trackDashboardInteraction('V3 Performance Event', {
      action: 'Sales Trend Range Changed',
      range,
    })
  }, [trackDashboardInteraction])
  const handleSalesAndInventoryViewChange = React.useCallback((view: SalesAndInventoryView) => {
    setSalesAndInventoryView(view)
    trackDashboardInteraction('V3 Performance Event', {
      action: 'Sales and Inventory View Changed',
      view,
    })
  }, [trackDashboardInteraction])
  const handlePerformanceLinkClick = React.useCallback((source: string, targetPath: string) => {
    trackDashboardInteraction('V3 Performance Event', {
      action: 'Performance Link Clicked',
      source,
      targetPath,
    })
  }, [trackDashboardInteraction])
  const handleReportLinkClick = React.useCallback((source: string, extra: Record<string, unknown> = {}) => {
    trackDashboardInteraction('V3 Report Event', {
      action: 'Reports Link Clicked',
      source,
      ...extra,
    })
  }, [trackDashboardInteraction])
  const handleOpenToBuyAskIndie = React.useCallback(() => {
    trackDashboardInteraction('V3 Ask Indie Event', {
      action: 'Open Clicked',
      source: 'otb_kpi',
      context: 'open_to_buy',
      calculationMode: openToBuyCalculationMode,
      valueMode: openToBuyValue,
    })
    requestOrbitIndiePanelOpen({ source: 'otb-kpi', prompt: openToBuyIndiePrompt, submit: true })
  }, [openToBuyCalculationMode, openToBuyIndiePrompt, openToBuyValue, trackDashboardInteraction])

  return (
    <ClientDashboardV3Layout activeTab="dashboard">
      <DashboardFilterBar
        filters={dashboardFilters}
        locationOptions={locationOptions}
        categoryOptions={categoryOptions}
        classOptions={classOptions}
        disabled={!currentClient?.Id}
        fullBleed
        className="-mt-6"
        onChange={setDashboardFilters}
      />

      <div className="mt-[14px] space-y-6 pb-10">
        {loading && (
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-sm text-gray-500">Loading dashboard data…</div>
          </section>
        )}

        {!loading && !currentClient?.Id && (
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-sm text-gray-500">Select a client to load the dashboard.</div>
          </section>
        )}

        {!loading && currentClient?.Id && currentMonth && (
          <>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1">
                  <h2 className="!text-[17px] !font-semibold !leading-none text-[var(--card-foreground,#101828)]">
                    Your Monthly recap {currentMonth ? EpochMonthService.epochMonthToMMMYY(currentMonth.epochMonthSpan) : ''}
                  </h2>
                  <p className="!text-[13px] !font-normal !text-[#667085]">
                    A month of strong signals and a few gaps worth addressing.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div aria-label="Monthly recap range" className="flex items-center gap-1 rounded-[10px] border border-[#E9EAEB] bg-[#F9FAFB] p-1">
                    {MONTHLY_RECAP_VISIBLE_RANGES.map((range) => {
                      const isSelected = monthlyRecapRange === range

                      return (
                        <button
                          key={range}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => handleMonthlyRecapRangeChange(range)}
                          className={`flex h-7 items-center justify-center gap-1.5 rounded-[8px] px-3 text-[12px] font-medium transition-all ${isSelected
                            ? 'border border-[#E9EAEB] bg-white text-[#7F56D9] shadow-xs font-semibold'
                            : 'text-[#667085] hover:text-[#101828]'
                            }`}
                        >
                          {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-[#7F56D9]" />}
                          {range}
                        </button>
                      )
                    })}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-normal text-[#667085]">Show:</span>
                    <div aria-label="Monthly recap comparison" className="flex items-center gap-1 rounded-[10px] border border-[#E9EAEB] bg-[#F9FAFB] p-1">
                      {MONTHLY_RECAP_COMPARISONS.map((comparison) => {
                        const isSelected = monthlyRecapComparison === comparison
                        const displayLabel = comparison

                        return (
                          <button
                            key={comparison}
                            type="button"
                            aria-label={comparison}
                            aria-pressed={isSelected}
                            onClick={() => handleMonthlyRecapComparisonChange(comparison)}
                            className={`flex h-7 items-center justify-center gap-1.5 rounded-[8px] px-3 text-[12px] font-medium transition-all ${isSelected
                              ? 'border border-[#E9EAEB] bg-white text-[#7F56D9] shadow-xs font-semibold'
                              : 'text-[#667085] hover:text-[#101828]'
                              }`}
                          >
                            {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-[#7F56D9]" />}
                            {displayLabel}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-normal text-[#667085]">
                      Trend % through month
                    </span>
                    <div
                      aria-label="Trend % through month container"
                      className="flex h-7 w-[68px] flex-shrink-0 items-center justify-end rounded-[6px] border border-[#D5D7DA] bg-[#F2F4F7] px-2 font-mono text-[13px] text-[#101828] focus-within:border-[#7F56D9] focus-within:ring-1 focus-within:ring-[#7F56D9]"
                    >
                      <span
                        aria-label="Trend % through month"
                        className="w-full text-right font-mono text-[13px] text-[#101828]"
                      >
                        {monthlyRecapTrendPercentLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {recapCards.map((card) => (
                  <section key={card.label} className="flex min-w-0 flex-col justify-between rounded-[20px] border border-[#EAECF0] bg-white p-3 shadow-xs">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <h3 className="!text-[14px] !font-medium !leading-5 !text-[#344054] !mb-0">{card.label}</h3>
                          {resolveMonthlyRecapTooltipKey(card.label) && (
                            <KpiHelpIcon tooltipKey={resolveMonthlyRecapTooltipKey(card.label) as KpiTooltipKey} className="self-center" />
                          )}
                        </div>
                        <div className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 !text-[12px] !font-semibold leading-tight ${card.comparisonPositive ? 'bg-[#ECFDF5] text-[#027A48]' : 'bg-[#FCEBEB] text-[#D92D20]'}`}>
                          <span className="opacity-80">{monthlyRecapComparison === 'vs Plan' ? 'Plan' : 'LY'}</span>
                          <span>{card.comparison}</span>
                        </div>
                      </div>

                      <div className="mt-3 mb-2 flex flex-col justify-center">
                        <div className="!text-[32px] !font-bold leading-none tracking-tight !text-[#101828]">{card.value}</div>
                        {card.label === 'Sales' && card.trendLabel ? (
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 !text-[12px] leading-none">
                            <span className="!font-semibold !text-[#344054]">
                              {card.trendLabel}
                            </span>
                            {isSalesDynamicTrendEnabled && (
                              <>
                                <span className="text-[#98A2B3]">•</span>
                                <button
                                  type="button"
                                  onClick={() => setSalesDynamicTrendModalOpen(true)}
                                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap !text-[14px] !font-medium text-[#7F56D9] transition-colors hover:text-[#6941C6] hover:underline"
                                  data-testid="sales-card-dynamic-trend-link"
                                >
                                  <TrendingUp className="h-3.5 w-3.5" />
                                  View dynamic trend
                                </button>
                                <span className="rounded-full bg-[#ECFDF3] px-2 py-0.5 text-[11px] font-semibold text-[#079455]">
                                  New
                                </span>
                              </>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <div className="my-3.5 border-t border-[#F2F4F7]" />
                      {monthlyRecapComparison === 'vs Plan' ? (
                        <div className="space-y-2">
                          {card.planValue && card.planAchievementPercent !== null ? (
                            <>
                              <div className="flex items-center justify-between gap-3 text-xs leading-[18px]">
                                <span className="!text-[12px] !text-[#667085]">Plan</span>
                                <span className="!text-[14px] !font-medium !text-[#344054]">{card.planValue}</span>
                              </div>
                              <div className="flex items-center justify-between gap-3 text-xs leading-[18px]">
                                <span className="!text-[12px] !text-[#667085]">{card.label === 'Inventory' || card.label === 'Markdowns' ? '% of plan (lower is better)' : '% of plan achieved'}</span>
                                <span className="!text-[14px] !font-medium !text-[#344054]">{Math.round(card.planAchievementPercent)}%</span>
                              </div>
                              <div className="mt-2">
                                <RecapAchievementBar
                                  label={card.label}
                                  percent={card.planAchievementPercent}
                                  descriptor={card.label === 'Inventory' || card.label === 'Markdowns' ? '% of plan (lower is better)' : '% of plan achieved'}
                                />
                              </div>
                            </>
                          ) : (
                            <p className="!text-[11px] leading-[16.5px] !text-[#667085]">No plan loaded</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {card.lastYearValue && card.lastYearAchievementPercent !== null ? (
                            <>
                              <div className="flex items-center justify-between gap-3 text-xs leading-[18px]">
                                <span className="!text-[12px] !text-[#667085]">Last year</span>
                                <span className="!text-[14px] !font-medium !text-[#344054]">{card.lastYearValue}</span>
                              </div>
                              <div className="flex items-center justify-between gap-3 text-xs leading-[18px]">
                                <span className="!text-[12px] !text-[#667085]">{card.label === 'Inventory' || card.label === 'Markdowns' ? '% of last year (lower is better)' : '% of last year achieved'}</span>
                                <span className="!text-[14px] !font-medium !text-[#344054]">{Math.round(card.lastYearAchievementPercent)}%</span>
                              </div>
                              <div className="mt-2">
                                <RecapAchievementBar
                                  label={card.label}
                                  percent={card.lastYearAchievementPercent}
                                  descriptor={card.label === 'Inventory' || card.label === 'Markdowns' ? '% of last year (lower is better)' : '% of last year achieved'}
                                />
                              </div>
                            </>
                          ) : (
                            <p className="!text-[11px] leading-[16.5px] !text-[#667085]">No last-year comparison loaded</p>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-end">
                <Link
                  to={resolvedClientCode ? buildV3ClientDashboardPerformancePath(resolvedClientCode) : '#'}
                  onClick={() => handlePerformanceLinkClick('monthly_recap', resolvedClientCode ? buildV3ClientDashboardPerformancePath(resolvedClientCode) : '#')}
                  className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#7F56D9] transition-colors hover:text-[#6941C6] hover:underline focus:outline-none"
                  data-testid="view-more-in-performance-link"
                >
                  View more in performance →
                </Link>
              </div>
            </section>

            <SalesAndInventoryPerformanceSection
              activeView={salesAndInventoryView}
              onViewChange={handleSalesAndInventoryViewChange}
              range={salesTrendRange}
              onRangeChange={handleSalesTrendRangeChange}
              title={salesAndInventoryView === 'sales' ? 'Sales Performance' : 'Inventory Performance'}
              subtitle={
                salesAndInventoryView === 'sales'
                  ? `How actual sales compared to plan and last year over the past ${salesTrendRange === '6m' ? '6' : salesTrendRange === '12m' ? '12' : '3'} months.`
                  : `How actual inventory compared to plan and last year over the past ${salesTrendRange === '6m' ? '6' : salesTrendRange === '12m' ? '12' : '3'} months.`
              }
              summaryCards={
                salesAndInventoryView === 'sales'
                  ? targetStats.map((s) => ({ label: s.label, value: s.value, sub: s.sub, tone: s.tone }))
                  : inventoryTrendSummaryCards.map((s) => ({ label: s.label, value: s.value, sub: s.sub, tone: s.tone }))
              }
              chartData={
                salesAndInventoryView === 'sales'
                  ? salesTrendChartData
                  : inventoryTrendChartPoints
              }
              emptyMessage={
                salesAndInventoryView === 'sales'
                  ? 'No completed-month sales trend points are available for the selected client yet.'
                  : 'No completed-month inventory trend points are available for the selected client yet.'
              }
              analysisHref={resolvedClientCode ? buildV3ClientDashboardPerformancePath(resolvedClientCode) : null}
              onAnalysisClick={() => handlePerformanceLinkClick('sales_inventory_analysis', buildV3ClientDashboardPerformancePath(resolvedClientCode))}
              hasFilters={hasActiveDashboardFilters}
            />

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <section className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <h3 className={HUB_TILE_TITLE_CLASS_NAME}>Performance</h3>
                  {healthSummary?.askIndiePrompt && (
                    <button
                      type="button"
                      onClick={() => requestOrbitIndiePanelOpen({
                        source: 'home-performance',
                        prompt: healthSummary.askIndiePrompt,
                        submit: true,
                      })}
                      className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-[#534AB7] hover:underline"
                    >
                      <Sparkles className="h-4 w-4" />
                      Ask Indie
                    </button>
                  )}
                  {/* <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${performanceGauge.statusClassName}`}>
                          {performanceGauge.statusLabel}
                        </span> */}
                </div>

                <HealthCashFlowSection simpleHealthView={true} onSummaryChange={setHealthSummary} />

                <div className="mt-auto border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-gray-500">
                      {healthSummary?.verticalLabel ?? 'Vertical'}
                      <KpiHelpIcon tooltipKey="verticalAverage" />
                      avg:
                      <strong className="font-semibold text-gray-900">{healthSummary?.verticalAverage ?? '—'}</strong>
                    </span>

                    {resolvedClientCode && (
                      <Link
                        to={buildV3ClientDashboardPerformancePath(resolvedClientCode)}
                        state={{ activeTab: 'Health & Cash flow' }}
                        onClick={() => handlePerformanceLinkClick('home_performance_tile', buildV3ClientDashboardPerformancePath(resolvedClientCode))}
                        className="inline-flex shrink-0 items-center gap-1 !text-[13px] !font-semibold !text-[#7F56D9] transition-colors hover:underline"
                      >
                        Score breakdown
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              </section>

              {reportsPanel && (

                <section className="flex h-full flex-col justify-between rounded-[20px] border border-[#EAECF0] bg-white p-4 shadow-xs">
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="!text-[18px] !font-bold !text-[#101828]">Reports to review</h3>
                      <span className="whitespace-nowrap rounded-full bg-[#F4F3FF] px-2.5 py-0.5 !text-[12px] !font-medium !text-[#5925DC]">
                        {reportsPanel.total !== null && reportsPanel.total !== undefined ? `${reportsPanel.total} Total` : '— Total'}
                      </span>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-[#F79009]" />
                            <span className="!text-[14px] !font-semibold !text-[#101828]">OTB recap report</span>
                          </div>
                          <span className="!text-[12px] !font-normal !text-[#98A2B3]">Updated today</span>
                        </div>
                        <p className="mt-1 pl-4 !text-[12px] !font-normal leading-[18px] !text-[#667085]">
                          Your open-to-buy against plan, by class and location.
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-[#F79009]" />
                            <span className="!text-[14px] !font-semibold !text-[#101828]">Inventory action report</span>
                          </div>
                          <span className="!text-[12px] !font-normal !text-[#98A2B3]">Updated today</span>
                        </div>
                        <p className="mt-1 pl-4 !text-[12px] !font-normal leading-[18px] !text-[#667085]">
                          Slow-moving and overstocked classes that need a decision.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="my-4 border-t border-[#F2F4F7]" />
                    <div className="flex items-center justify-between gap-2">
                      <p className="!text-[12px] !font-normal !text-[#667085]">All up to date</p>
                      {reportsPanel.href ? (
                        <Link
                          to={reportsPanel.href}
                          onClick={() => handleReportLinkClick('reports_summary_tile', { targetPath: reportsPanel.href })}
                          className="inline-flex items-center gap-1 !text-[13px] !font-semibold !text-[#7F56D9] transition-colors hover:underline"
                        >
                          Open library
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-1 !text-[13px] !font-semibold !text-gray-400">
                          Open library
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>

                  </div>
                </section>
              )}
              <section className="flex min-h-[264px] flex-col rounded-2xl border border-[#EAECF0] bg-white shadow-sm">
                <div className="px-6 pb-4 pt-[1.25rem]">
                  <h3 className="!text-[18px] !font-bold !text-[#101828]">Ask Indie</h3>
                  <p className="mt-3 text-sm leading-6 text-[#667085]">
                    Ask Indie answers questions about your business,{' '}
                    {resolvedClientCode ? (
                      <Link
                        to={buildV3ClientIndieAiPath(resolvedClientCode)}
                        state={{ source: 'dashboard-ask-indie' }}
                        onClick={() => trackDashboardInteraction('V3 Ask Indie Event', { action: 'Open Clicked', source: 'dashboard_text_link', targetPath: buildV3ClientIndieAiPath(resolvedClientCode) })}
                        className="font-medium text-purple-700 no-underline hover:text-purple-800 hover:underline"
                      >
                        surfaces insights and recommended actions automatically,
                      </Link>
                    ) : (
                      'surfaces insights and recommended actions automatically,'
                    )}{' '}
                    and turns them into next steps you can act on immediately.
                  </p>
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-[#EAECF0] px-6 py-4">
                  <span className="text-xs leading-[18px] text-[#667085]">Open the full Ask Indie page</span>
                  {resolvedClientCode ? (
                    <Link
                      to={buildV3ClientIndieAiPath(resolvedClientCode)}
                      state={{ source: 'dashboard-ask-indie' }}
                      onClick={() => trackDashboardInteraction('V3 Ask Indie Event', { action: 'Open Clicked', source: 'dashboard_cta', targetPath: buildV3ClientIndieAiPath(resolvedClientCode) })}
                      className="inline-flex items-center gap-1 text-sm font-medium text-purple-700 no-underline hover:text-purple-800"
                    >
                      Try Ask Indie
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-400">
                      Try Ask Indie
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>
              </section>
              <section className="flex h-full flex-col justify-between rounded-[20px] border border-[#EAECF0] bg-white p-4 shadow-xs">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="!text-[18px] !font-bold !text-[#101828]">Your M1 Journey</h3>
                    <span className="whitespace-nowrap rounded-full bg-[#ECFDF5] px-2.5 py-0.5 !text-[12px] !font-medium !text-[#027A48] inline-flex items-center gap-1">
                      <span className="text-[10px]"></span> Together {yearsTogether} {yearsTogether === 1 ? 'year' : 'years'}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F4F3FF] !text-[14px] !font-semibold !text-[#7F56D9]">
                      M1
                    </div>
                    <span className="!text-[14px] !font-bold !text-[#101828]">
                      {m1JourneyDateRange}
                    </span>
                  </div>

                  <div className="mt-4 space-y-3">
                    {dashboardNotifications.journey.map((notification) => (
                      <div key={notification.id} className="flex items-start justify-between gap-3">
                        <div>

                          <div className="mt-1 !text-[12px] !font-normal leading-[18px] !text-[#667085]">{notification.message}</div>
                        </div>

                      </div>
                    ))}
                    {!dashboardNotificationsLoading && dashboardNotifications.journey.length === 0 && (
                      <p className="!text-[13px] !font-normal leading-[20px] !text-[#475467]">
                        {yearsTogether === 1 ? 'One year' : yearsTogether === 2 ? 'Two years' : yearsTogether === 3 ? 'Three years' : `${yearsTogether} years`} of planning, {yearsTogether * 2} buying seasons, and a lot of good decisions made with real numbers instead of gut feel.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="my-4 border-t border-[#F2F4F7]" />
                  <p className="!text-[12px] !font-normal !text-[#98A2B3]">
                    Milestones update automatically as you progress
                  </p>
                </div>
              </section>
            </div>
            <section className="grid gap-6 xl:grid-cols-3">
              <div className="space-y-6">
                <section className="relative overflow-visible rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-start justify-between gap-3 px-6 pb-3 pt-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="dashboard-section-heading">Open to buy</h3>
                        <KpiHelpIcon tooltipKey="openToBuy" />
                        {(openToBuyLocation || openToBuyCategory || openToBuyClass) && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-3 py-1 text-xs font-medium text-[#047857] border border-[#A7F3D0]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                            Filters applied
                          </span>
                        )}
                      </div>
                      <p className="mt-1 dashboard-section-subheading">Current position through {EpochMonthService.epochMonthToMMMYY(openToBuyEndingEpochMonth)}</p>
                    </div>
                    <div ref={openToBuyControlsRef} className="relative flex shrink-0 items-center gap-2">
                      <label className="relative">
                        <span className="sr-only">Open to Buy ending period</span>
                        <select
                          aria-label="Open to Buy ending period"
                          className="h-8 max-w-32 appearance-none rounded-md border border-[#D0D5DD] bg-white py-1 pl-3 pr-7 text-xs font-medium text-[#344054] outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                          value={openToBuyEndingPeriod}
                          onChange={(event) => setOpenToBuyEndingPeriod(event.target.value)}
                        >
                          {endingPeriodOptions.slice(0, 12).map((period) => (
                            <option key={period.value} value={Number(period.value) === baseEpochMonth ? '' : period.value}>{period.label}</option>
                          ))}
                        </select>
                        <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#667085]" />
                      </label>
                      <button
                        type="button"
                        aria-label="Open Open to Buy filters"
                        aria-expanded={openToBuyFiltersOpen}
                        aria-haspopup="dialog"
                        onClick={() => {
                          setDraftOpenToBuyLocation(openToBuyLocation)
                          setDraftOpenToBuyCategory(openToBuyCategory)
                          setDraftOpenToBuyClass(openToBuyClass)
                          setOpenToBuyFiltersOpen((isOpen) => !isOpen)
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D0D5DD] bg-white text-[#344054] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-100"
                      >
                        <SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
                      </button>
                      {openToBuyFiltersOpen && (
                        <div aria-label="Open to Buy filters" role="dialog" className="absolute right-0 top-10 z-40 w-[285px] rounded-md border border-[#EAECF0] bg-white shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.10),0px_1px_3px_rgba(0,0,0,0.10)] xl:left-full xl:right-auto xl:top-0 xl:ml-3">
                          <div className="border-b border-[#EAECF0] p-3"><h4 className="text-sm font-semibold text-[#101828]">Open to Buy filters</h4></div>
                          <div className="space-y-3 p-3">
                            <div>
                              <div className="flex items-center gap-1 text-xs font-medium text-[#101828]">Calculation <span title="MTD uses the trended current-month OTB calculation and is useful when an MTD file is available. EOM uses the inventory-deviation OTB calculation and supports beginning-of-month planning."><CircleHelp className="h-3.5 w-3.5 text-[#667085]" /></span></div>
                              <div className="mt-1 flex rounded-md bg-[#F2F4F7] p-1">
                                {(['mtd', 'eom'] as const).map((mode) => <button key={mode} type="button" aria-pressed={openToBuyCalculationMode === mode} onClick={() => setOpenToBuyCalculationMode(mode)} className={`flex-1 rounded-md px-2 py-1 text-xs font-medium uppercase ${openToBuyCalculationMode === mode ? 'bg-white text-[#101828] shadow-sm' : 'text-[#667085]'}`}>{mode}</button>)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-[#101828]">Value</div>
                              <div className="mt-1 flex rounded-md bg-[#F2F4F7] p-1">
                                {(['retail', 'cost'] as const).map((value) => <button key={value} type="button" aria-pressed={openToBuyValue === value} onClick={() => setOpenToBuyValue(value)} className={`flex-1 rounded-md px-2 py-1 text-xs font-medium capitalize ${openToBuyValue === value ? 'bg-white text-[#101828] shadow-sm' : 'text-[#667085]'}`}>{value}</button>)}
                              </div>
                            </div>
                            <label className="block text-xs font-medium text-[#344054]">Location<select aria-label="Open to Buy filter location" className="mt-1 block h-8 w-full rounded-md border border-[#D0D5DD] bg-white px-3 text-xs" value={draftOpenToBuyLocation} onChange={(event) => setDraftOpenToBuyLocation(event.target.value)}><option value="">All locations</option>{locationOptions.map((item) => <option key={item.code} value={item.code}>{item.label || `${item.code} - ${item.name}`}</option>)}</select></label>
                            <label className="block text-xs font-medium text-[#344054]">Category<select aria-label="Open to Buy filter category" className="mt-1 block h-8 w-full rounded-md border border-[#D0D5DD] bg-white px-3 text-xs" value={draftOpenToBuyCategory} onChange={(event) => { setDraftOpenToBuyCategory(event.target.value); setDraftOpenToBuyClass('') }}><option value="">All categories</option>{categoryOptions.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}</select></label>
                            <label className="block text-xs font-medium text-[#344054]">Class<select aria-label="Open to Buy filter class" className="mt-1 block h-8 w-full rounded-md border border-[#D0D5DD] bg-white px-3 text-xs" value={draftOpenToBuyClass} onChange={(event) => setDraftOpenToBuyClass(event.target.value)}><option value="">All classes</option>{classOptions.filter((item) => !draftOpenToBuyCategory || item.category === draftOpenToBuyCategory).map((item) => <option key={item.code} value={item.code}>{item.label || `${item.code} - ${item.name}`}</option>)}</select></label>
                          </div>
                          <div className="flex justify-end gap-2 px-3 pb-3"><button type="button" onClick={() => setOpenToBuyFiltersOpen(false)} className="h-7 rounded-md bg-[#F2F4F7] px-2 text-xs font-medium text-[#344054]">Dismiss</button><button type="button" onClick={() => { setOpenToBuyLocation(draftOpenToBuyLocation); setOpenToBuyCategory(draftOpenToBuyCategory); setOpenToBuyClass(draftOpenToBuyClass); setOpenToBuyFiltersOpen(false) }} className="h-7 rounded-md bg-[#7F56D9] px-2 text-xs font-medium text-white hover:bg-[#6941C6]">Apply</button></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-6 pb-5">
                    {openToBuyLoading && <div className="flex h-36 items-center text-sm text-[#667085]">Loading open-to-buy position…</div>}
                    {!openToBuyLoading && openToBuyError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{openToBuyError}</div>}
                    {!openToBuyLoading && !openToBuyError && (!openToBuyData || Number(openToBuyData.rowCount) === 0) && <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-[#667085]">No open-to-buy data is available for the selected period and filters.</div>}
                    {!openToBuyLoading && !openToBuyError && openToBuyData && Number(openToBuyData.rowCount) > 0 && <>
                      <div className={`text-[32px] font-semibold leading-none tracking-tight ${openToBuyKpi.remaining < 0 ? 'text-red-700' : 'text-[#101828]'}`}>{PerformanceDashboardService.moneyFormat(openToBuyKpi.remaining, true)}</div>
                      <p className="mt-2 text-xs text-[#667085]">Remaining open to buy · {openToBuyValue} · {openToBuyCalculationMode.toUpperCase()}{openToBuyCalculationMode === 'mtd' && openToBuyIncludeCurrentMonthDeviation ? ' · incl. current month deviation' : ''}</p>
                      <div className="mt-6 grid grid-cols-3 gap-3">{[
                        { label: 'Planned', value: openToBuyKpi.planned, percent: openToBuyKpi.plannedPercent, color: 'bg-[#7F56D9]' },
                        { label: 'On order', value: openToBuyKpi.onOrder, percent: openToBuyKpi.onOrderPercent, color: 'bg-[#F79009]' },
                        { label: 'Remaining', value: openToBuyKpi.remaining, percent: openToBuyKpi.remainingPercent, color: openToBuyKpi.remaining < 0 ? 'bg-red-500' : 'bg-[#12B76A]' },
                      ].map((item) => <div key={item.label}><div className="flex h-1.5 overflow-hidden rounded-full bg-gray-100"><div className={item.color} style={{ width: `${item.percent}%` }} /></div><div className="mt-2 text-base font-semibold text-[#101828]">{PerformanceDashboardService.moneyFormat(item.value, true)}</div><div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[#667085]">{item.label}</div></div>)}</div>
                    </>}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-200 bg-gray-50 px-6 py-3 text-xs font-medium">
                    {resolvedClientCode && resolvedClientId ? <Link to={buildV3ClientDashboardReportsPath(resolvedClientCode, resolvedClientId)} onClick={() => handleReportLinkClick('open_to_buy_footer', { targetPath: buildV3ClientDashboardReportsPath(resolvedClientCode, resolvedClientId) })} className="text-purple-700 no-underline hover:text-purple-800">Open reports <ArrowRight className="ml-1 inline h-3 w-3" /></Link> : <span className="text-gray-400">Open reports</span>}
                    {resolvedClientCode && <button
                      type="button"
                      disabled={openToBuyLoading || Boolean(openToBuyError) || !openToBuyData || Number(openToBuyData.rowCount) === 0}
                      onClick={handleOpenToBuyAskIndie}
                      className="border-0 bg-transparent p-0 text-purple-700 hover:text-purple-800 disabled:cursor-not-allowed disabled:text-gray-400"
                    ><Sparkles className="mr-1 inline h-3 w-3" />Ask Indie about my OTB</button>}
                  </div>
                </section>

                <section className="relative flex min-h-[240px] flex-col overflow-hidden rounded-2xl border border-[#E4E7EC] bg-[linear-gradient(135deg,#F5F3FF_0%,#FFFFFF_48%,#EFF8FF_100%)] p-6 shadow-sm">
                  <div className="pointer-events-none absolute -right-8 top-0 h-28 w-28 rounded-full bg-[#DDD6FE]/50 blur-2xl" />
                  <div className="pointer-events-none absolute bottom-0 left-0 h-24 w-24 rounded-full bg-[#BFDBFE]/35 blur-2xl" />
                  <div className="relative flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6941C6]">Retail Insights</span>
                    <span className="inline-flex items-center rounded-full bg-[#ECFDF3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-[#027A48]">New Launch</span>
                  </div>

                  <div className="relative mt-4 flex flex-1 flex-col">
                    <h3 className="max-w-[16rem] text-[24px] font-semibold leading-8 tracking-[-0.02em] text-[#101828]">New: the Retail Insights blog</h3>
                    <p className="mt-3 max-w-[18rem] text-sm leading-6 text-[#475467]">Practical tips and trend breakdowns from people who&apos;ve run retail floors.</p>

                    <div className="mt-auto pt-5">
                      <a
                        href={RETAIL_INSIGHTS_BLOG_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-[#7F56D9] px-4 py-2.5 text-sm font-medium text-white no-underline shadow-[0_1px_3px_0_rgba(16,24,40,0.12)] transition-colors hover:bg-[#6941C6] hover:text-white"
                      >
                        Visit the blog
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </section>
              </div>

              <section className="flex min-h-[264px] flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className={HUB_TILE_TITLE_CLASS_NAME}>Report Insights</h3>
                    <p className="mt-1 text-sm text-gray-600">Recommended for stores like yours.</p>
                  </div>
                  {reportsPanel?.href ? (
                    <Link to={reportsPanel.href} onClick={() => handleReportLinkClick('report_insights_view_all', { targetPath: reportsPanel.href })} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 no-underline hover:text-purple-800">
                      View all
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                      View all
                      <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-1 pt-5">
                  {REPORT_INSIGHT_ROWS.map((row, index) => {
                    const Icon = row.icon
                    const rowLinkState = row.openReport ? { openReport: row.openReport } : undefined

                    return (
                      <div key={row.id} className={`flex items-center gap-3 py-3 ${index !== REPORT_INSIGHT_ROWS.length - 1 ? 'border-b border-[#F3F4F6]' : ''}`}>
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${row.iconWrapperClassName}`}>
                          <Icon className={`h-4 w-4 ${row.iconClassName}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium leading-5 text-[#101828]">{row.title}</span>
                            <span className={`inline-flex items-center gap-[3px] rounded px-1.5 py-px text-[10px] font-medium leading-[15px] tracking-[0.117px] ${row.badgeClassName}`}>
                              {row.badgeSparkle && <Sparkles className="h-[9px] w-[9px] shrink-0" />}
                              {row.badgeLabel}
                            </span>
                          </div>
                        </div>
                        {reportsPanel?.href ? (
                          <Link to={reportsPanel.href} state={rowLinkState} onClick={() => handleReportLinkClick('report_insight_card', { reportInsightId: row.id, reportInsightTitle: row.title, targetPath: reportsPanel.href, openReport: row.openReport || '' })} className="shrink-0 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-xs font-medium leading-[18px] text-[#374151] no-underline hover:bg-gray-50 hover:text-[#374151]">
                            Open
                          </Link>
                        ) : (
                          <span className="shrink-0 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-1.5 text-xs font-medium leading-[18px] text-gray-400">
                            Open
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="border-t border-gray-200 pt-4">
                  {reportsPanel?.href ? (
                    <Link to={reportsPanel.href} onClick={() => handleReportLinkClick('report_insights_footer', { targetPath: reportsPanel.href })} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#7F56D9] px-4 py-2.5 text-sm font-medium leading-5 text-white no-underline shadow-[0_1px_3px_0_rgba(16,24,40,0.10)] transition-colors hover:bg-[#6941C6] hover:text-white">
                      Go to reports library
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (

                    <span className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium leading-5 text-gray-400">
                      Go to reports library
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </div>
              </section >

              <SalesDynamicTrendModal
                open={isSalesDynamicTrendModalOpen}
                onOpenChange={setSalesDynamicTrendModalOpen}
                clientId={currentClient?.Id ?? null}
                epochMonth={baseEpochMonth}
                monthLabel={salesDynamicTrendMonthLabel}
                locationOptions={salesDynamicTrendLocationOptions}
              />

              <section className="flex min-h-[264px] flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div>
                  <h3 className={HUB_TILE_TITLE_CLASS_NAME}>M1 Events</h3>
                  <p className="mt-1 text-sm text-gray-600">Recent activity and upcoming milestones.</p>
                </div>
                <div className="mt-5 flex flex-1 flex-col gap-3">
                  {dashboardNotifications.event.map((notification) => {
                    const dateParts = formatNotificationDateParts(notification.event_date || notification.release_at || notification.created_at)

                    return (
                      <div key={notification.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex w-11 shrink-0 flex-col items-center rounded-lg bg-white px-2 py-1.5 text-center shadow-sm">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{dateParts?.month || 'M1'}</span>
                            <span className="text-base font-semibold leading-none text-[#D92D20]">{dateParts?.day || '—'}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900">{notification.title}</div>
                            <div
                              className="mt-1 text-xs leading-5 text-gray-600 [&_a]:font-medium [&_a]:text-purple-700 [&_a]:underline [&_a:hover]:text-purple-800"
                              dangerouslySetInnerHTML={{ __html: sanitizeNotificationHtml(notification.message) }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  {!dashboardNotificationsLoading && dashboardNotifications.event.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-500">No M1 events are active for this client.</div>
                  )}
                </div>
              </section>
            </section >
          </>
        )
        }
      </div >
    </ClientDashboardV3Layout >
  )
}
